// apps/admin/src/screens/achievements.js
//
// Achievements admin screen (spec 12): the list with displayed statuses; the
// detail with versions and their comparison, marks with flipbooks, the check
// timeline, checkpoints, live tests and the audit log, and the item actions.
// Errors are classified once (classifyAuthoringResponse); each load and action
// is one admin-lifecycle operation; server strings reach the DOM only through
// esc() or textContent.
import { api } from '../api.js';
import { navigate } from '../router.js';
import { ensureRecentAdminAuth } from '../admin-security.js';
import { createPaginationNavigationGuard } from '../pagination-navigation.js';
import { createOperation, DEFAULT_OP_DEADLINE_MS, navAbort, whenAborted } from '../lib/admin-lifecycle.js';
import { ApiError } from '../../../../src/shared/api-fetch.js';
import { esc } from '../../../../src/shared/escape-html.js';
import { formatDateTime } from '../../../../src/shared/format-time.js';
import { systemTag } from '../../../../src/shared/system-label.js';
import { AUTHORING_ERROR_CODES, classifyAuthoringResponse } from '../../../../src/shared/authoring-retry.js';
import {
  ACTOR_KINDS, AUDIT_ACTIONS, AUTHORING_SYSTEMS, CHECK_REASONS, EVENT_TYPES, FILE_CAPS, FILE_KINDS, FILE_STATES, FORMAT_RE, ITEM_KINDS,
  ITEM_STATUSES, LIMITS, MARK_ROLES, POLARITIES, PUBLISH, SERVER, TEST_OUTCOMES, VERDICTS,
} from '../../../../src/shared/authoring-limits.js';
import { decodeFrames } from '../../../../src/shared/authoring-evidence-format.js';

const API = '/api/admin/authoring';
const PAGE_SIZE = 50;
const HISTORY_PAGE_SIZE = 20;
const RETRY_DELAYS_MS = Object.freeze([1000, 3000]);
const FLIP_INTERVAL_MS = 100;
// Spec 7.1: a publication's transaction has the 30 s write deadline, and its post-commit work (the
// change notice and the three refreshes) then runs under its own 10 s deadline before the answer
// is sent; both, plus transit, so the client never gives up on an answer the server may still send.
const MUTATION_DEADLINE_MS = SERVER.writeDeadlineMs + SERVER.postCommitDeadlineMs + 5_000;
// A frame sequence is at most its stored cap (spec 6.5); never read more.
const FRAMES_DOWNLOAD_MAX_BYTES = FILE_CAPS.frames.storedBytes;
// An error body is a small { error, ...details } object; read no more than this of one.
const ERROR_BODY_MAX_BYTES = 4_096;
const CONTENT_LENGTH_RE = /^[0-9]{1,12}$/;
// A body cancel is awaited at most this long, and never past its operation's deadline.
const CANCEL_WAIT_MAX_MS = 1_000;
// A stream that keeps answering with empty chunks is not making progress: this many are allowed, then it is refused.
const EMPTY_CHUNKS_MAX = 16;
// While a body streams in without a pause, control returns to the event loop this often, so timers can fire.
const READ_YIELD_MS = 16;
// The last failures with their underlying errors, for diagnosis; the admin sees messages from the code only.
// Bounded by entries and by retained bytes (two per character kept, plus each record's own share); each
// cause is kept as a bounded record, never as the error itself (noteDiagnostic).
const DIAGNOSTICS_MAX = 20;
const DIAGNOSTICS_BYTES_MAX = 128 * 1024;
const DIAGNOSTIC_TEXT_MAX = 8_192;      // characters kept for one entry, across its whole cause chain
const DIAGNOSTIC_FIELD_MAX = 1_024;     // one name, message, code or stack
const DIAGNOSTIC_RECORDS_MAX = 16;      // errors kept for one entry: the cause, its causes, an AggregateError's errors
const DIAGNOSTIC_DEPTH_MAX = 4;         // causes followed below the first
const DIAGNOSTIC_ERRORS_MAX = 4;        // an AggregateError's errors kept
const DIAGNOSTIC_RECORD_BYTES = 64;
// Retained screen state is bounded: rows kept per history (past it, the first rows shown are let go
// and paging goes on), cursors kept for Prev.
const HISTORY_ROWS_MAX = 200;
const LIST_HISTORY_MAX = 500;
const ROM_FILENAME_MAX = 255;
const HISTORIES = Object.freeze(['versions', 'checks', 'tests', 'audit']);
const SECTIONS = Object.freeze(['summary', 'versions', 'marks', 'checks', 'tests', 'audit']);

export const STATUS_LABELS = Object.freeze({
  superseded: 'Superseded',
  needs_retest: 'Needs re-test',
  published: 'Published',
  unpublished: 'Unpublished',
  ready: 'Ready',
  draft: 'Draft',
});
const STATUS_TONES = { superseded: 'warn', needs_retest: 'warn', published: 'accent', unpublished: '', ready: 'accent', draft: '' };
const CHECK_LABELS = { none: 'No check', passed: 'Passed', failed: 'Failed', outdated: 'Outdated' };
const ROLE_LABELS = { event: 'Event', score_reading: 'Score reading', game_start: 'Game started', game_over: 'Game over', game_cancel: 'Quit without game over' };
const POLARITY_LABELS = { positive: 'This is it', negative: 'Not this' };
const EVENT_LABELS = {
  life_lost: 'Life lost', level_reached: 'Level reached', level_complete: 'Level complete', item_collected: 'Item collected',
  boss_defeated: 'Boss defeated', game_over: 'Game over', custom: 'Custom', score: 'Score',
};
const FILE_KIND_LABELS = { memory: 'Memory', frames: 'Frames', checkpoint_near: 'Near checkpoint', checkpoint_early: 'Early checkpoint', test_frames: 'Test frames' };
const FILE_STATE_LABELS = { absent: 'Not recorded', declared: 'Uploading', attached: 'Attached', tombstoned: 'Removed' };
const OUTCOME_LABELS = { fired: 'Fired', missed: 'Missed', score: 'Score' };
const VERDICT_LABELS = { pending: 'Pending', confirmed: 'Confirmed', false_positive: 'False positive', rejected: 'Rejected' };
const GATE_LABELS = { start: 'Start', submit: 'Submit', cancel: 'Cancel' };
const CHECK_REASON_LABELS = {
  fired_early: 'Fired early', not_fired: 'Did not fire', fired_on_negative: 'Fired on a Not this mark',
  fired_in_quiet: 'Fired in a quiet stretch', score_mismatch: 'Score did not match the typed score',
  no_zero_checkpoint: 'No checkpoint shows a zero score', not_evaluated: 'The recording was not evaluated',
  too_few_marks: 'Too few marks', conflicting_marks: 'Marks overlap with opposite labels',
};
const AUDIT_LABELS = {
  create: 'Created', new_version: 'New version', restore: 'Restored a version', check: 'Check recorded',
  test: 'Live test recorded', verdict: 'Verdict recorded', mark_abandoned: 'Mark abandoned', publish: 'Published',
  unpublish: 'Unpublished', republish: 'Republished', delete: 'Deleted', leaderboard_cleared: 'Leaderboard cleared',
  superseded_by_static: 'Superseded by a static score definition', reparented: 'Moved to another game',
};
const REFRESH_LABELS = { notice: 'change notice', authored_cache: 'authored cache', leaderboard_cache: 'leaderboard cache', enrichment: 'catalog enrichment' };
// Task 17's refresh report: every part must be reported, each { status: 'ok' } or { status: 'failed', code }.
const REFRESH_PARTS = Object.freeze(Object.keys(REFRESH_LABELS));
const REFRESH_CODE_RE = /^[a-z][a-z0-9_]{0,63}$/;
// A 2xx answer to a publish, unpublish or delete that does not say committed: true.
const UNCONFIRMED_MESSAGE = 'The server did not confirm the change, so this item was reloaded. Check whether it was saved before trying again.';
const NOT_READY_REASONS = {
  minimums: 'not enough marks',
  evidence: 'evidence is still uploading or missing',
  check: 'no passing check for the current evidence',
  false_positive: 'a live test reported a false positive',
  missed: 'a live test reported a miss',
  scope: 'no confirmed live test on a current core',
};
// One message per spec 7.3 catalogue code (a test walks AUTHORING_ERROR_CODES).
const ERROR_MESSAGES = {
  bad_param: 'That request had an invalid value.',
  invalid_version: 'This version does not pass validation. Fix it in author mode, then try again.',
  gate_required: 'A Commodore 64 score definition needs a leaderboard gate.',
  bad_role: 'That mark role does not fit this kind of item.',
  rom_mismatch: 'That ROM does not belong to this game.',
  leaderboard_choice_required: 'Choose whether to keep or clear the leaderboard.',
  confirmation_mismatch: 'The title you typed does not match the game title.',
  admin_step_up_required: 'Confirm your sign-in to continue.',
  not_found: 'This item was not found, or achievement authoring is turned off.',
  conflict: 'This item changed since you opened it. Review the latest version, then try again.',
  not_ready: 'This version is not ready to publish.',
  static_score_exists: 'This game already has a score definition from RetroAchievements or code.',
  score_item_exists: 'This game already has an authored score definition.',
  cache_capacity: 'Publishing would exceed the size limit for published authored items.',
  item_was_published: 'An item that was ever published cannot be deleted. Unpublish it instead.',
  not_published: 'This item is not published.',
  mark_limit: 'This item has reached its limit of marks.',
  test_limit: 'This version has reached its limit of live tests.',
  // The cap or account in the details picks the message (quotaMessage); this one is for a limit the screen does not know.
  quota_exceeded: 'This would pass an authoring limit, so nothing was changed.',
  evidence_exists: 'A different file was already uploaded for that slot.',
  evidence_mismatch: 'That file does not match what was recorded for it.',
  evidence_dependency: 'That file needs its memory recording first. Try again in a moment.',
  stale_evidence: 'The evidence changed since the last check. Run the check again.',
  verdict_already_set: 'That was already recorded.',
  // The studio's refusal: this screen names no account, so it never gets this one.
  account_changed: 'Another account signed in on this browser, so nothing was changed. Reload the page.',
  gone: 'This was already deleted or abandoned.',
  length_required: 'The upload did not say how large it was.',
  evidence_too_large: 'That file is larger than its limit, so it was not loaded.',
  rate_limited: 'Too many requests. Try again in a moment.',
  uploads_busy: 'The server is busy. Try again in a moment.',
  authored_cache_unavailable: 'Published items are still loading on the server. Try again in a moment.',
};
// Codes the screen itself produces (never sent by the server).
const CLIENT_ERROR_MESSAGES = {
  stream_unsupported: 'This browser cannot load that file safely. Use a current version of Chrome, Edge or Firefox.',
  stream_stalled: 'The file stopped arriving, so it was not loaded. Try again.',
  step_up_failed: 'The sign-in check did not finish, so nothing was changed. Try again.',
  response_invalid: 'The server sent an answer this screen cannot read. Reload the page and try again.',
  op_start_failed: 'This could not start, so nothing was sent. Reload the page and try again.',
};
// Account refusals from requireAdmin that sit outside the spec 7.3 catalogue. Task 1 classes them
// reauth, but a step-up sign-in cannot clear any of them, so they are never stepped up: the admin is
// told what to do instead.
const ACCOUNT_ERROR_MESSAGES = {
  legal_acknowledgement_required: 'Your account needs to accept the current terms. Open RetroPlay, review and accept them, then try again.',
  unauthorized: 'Your session ended. Sign in again, then reload this page.',
  forbidden: 'This account no longer has admin access.',
};
// quota_exceeded, by the details' `cap` (with its limit) or evidence `account` (L5-5): each names its own remedy.
const QUOTA_CAP_MESSAGES = {
  versions_per_item: [LIMITS.versionsPerItem, (limit) => `This item has reached its limit of ${limit} versions, so no new version can be added.`],
  items_per_game: [LIMITS.itemsPerGame, (limit) => `This game has reached its limit of ${limit} authored items.`],
  checks_per_item: [LIMITS.checksPerItem, (limit) => `This item has reached its limit of ${limit} recorded checks.`],
  labels_per_game_hash: [LIMITS.labelsPerGameHash, (limit) => `This ROM has reached its limit of ${limit} labels.`],
  display_seq: [LIMITS.displaySeqMax, (limit) => `This game has used all ${limit} published achievement positions, so this item cannot be published.`],
};
const EVIDENCE_FULL_MESSAGE = 'Evidence storage is full. Abandon marks you no longer need, then try again.';
const QUOTA_ACCOUNT_MESSAGES = {
  admin: EVIDENCE_FULL_MESSAGE,
  global: EVIDENCE_FULL_MESSAGE,
  // Removed files are kept as fences (spec 7.12), so abandoning marks would only add to them.
  fences: 'Evidence storage has reached its limit for removed files, so no new file can be recorded. Contact the site owner.',
};
// A mutation whose outcome the server did not settle (L1-2): it is never resent, and the item is reloaded.
const UNKNOWN_OUTCOME_MESSAGE = 'The server may have saved that change. Check the item below before trying again.';
const TIMEOUT_MESSAGE = 'The server took too long to answer. Try again.';
const FRAMES_TIMEOUT_MESSAGE = 'The frames took too long to load. Try again.';
const RENDER_FAILED_MESSAGE = 'This could not be shown. Reload the page and try again.';
const FRAMES_RENDER_MESSAGE = 'These frames could not be shown.';
const CONTINUATION_FAILED_MESSAGE = 'The answer could not be shown here. Reload this item to see whether the change was saved.';
// Bounds of a valid answer: a page cursor (decodeAdminListCursor reads at most 256 characters), a mark's files (one per kind).
const CURSOR_MAX = 256;
const TEXT_MAX = 4_096;   // any one text field of an answer
const MARK_FILES_MAX = 5;
// A version's definitions (trigger, score definition, gate) are part of a delivery payload of at most
// LIMITS.payloadBytesMax encoded bytes, where every JSON value or key takes at least 2: their structure bound.
const DEFINITION_ELEMENTS_MAX = LIMITS.payloadBytesMax / 2;
const DETAILS_ELEMENTS_MAX = 4_096;   // an audit row's details
const JSON_DEPTH_MAX = 32;
// Spec 10.5's check result: its fields and its gate's three entries.
const CHECK_RESULT_FIELDS = Object.freeze(['passed', 'engineVersion', 'perMark', 'gate']);
const GATE_ROLES = Object.freeze(['start', 'submit', 'cancel']);
const MUTATION_TIMEOUT_MESSAGE = 'The server took too long to answer, so the change may have been saved. Check the item below before trying again.';
const COMPARE_FIELDS = [
  ['name', 'Name'], ['description', 'Description'], ['icon', 'Icon'], ['points', 'Points'],
  ['event_type', 'Event type'], ['trigger', 'Trigger'], ['score_spec', 'Score definition'],
  ['leaderboard_gate', 'Leaderboard gate'], ['dsl_version', 'DSL version'], ['payload_bytes', 'Payload bytes'],
  ['manual_edit', 'Edited by hand'],
];
// Definitions can outgrow their cell's 240px: those cells scroll, so the keyboard can reach them (WCAG 2.1.1).
const SCROLLED_FIELDS = new Set(['trigger', 'score_spec', 'leaderboard_gate']);

// ── Pure helpers ──

/** `map[key]` only for the map's own keys, so a server string such as `constructor` never reads a prototype member. */
function own(map, key) {
  return typeof key === 'string' && Object.hasOwn(map, key) ? map[key] : undefined;
}

/**
 * Spec 12 precedence: Superseded, Needs re-test, Published or Unpublished, Ready, Draft.
 * Task 16 computes needs_retest from published_version_id and its scope, which survive an
 * unpublish (spec 7.5), so an unpublished item whose published version is on a stale core
 * reads Needs re-test (spec 11.4) before anyone republishes it.
 */
export function displayedStatus(item) {
  let key = 'draft';
  if (item?.superseded === true) key = 'superseded';
  else if (item?.needs_retest === true) key = 'needs_retest';
  else if (item?.status === 'published') key = 'published';
  else if (item?.status === 'unpublished') key = 'unpublished';
  else if (item?.ready === true) key = 'ready';
  return { key, label: STATUS_LABELS[key] };
}

/** A published item whose current version differs from its published version. */
export function hasNewerVersion(item) {
  return item?.status === 'published' && item.published_version_id != null && item.current_version_id != null
    && String(item.current_version_id) !== String(item.published_version_id);
}

/** The item's name standing alone (the detail heading, the list cell). */
function itemName(item) {
  return item?.name || (item?.kind === 'score' ? 'Score definition' : 'Untitled achievement');
}

/** The item's name inside a sentence (dialog titles, results): sentence case, so an unnamed item reads in lower case. */
function itemPhrase(item) {
  return item?.name || (item?.kind === 'score' ? 'the score definition' : 'the untitled achievement');
}

/** A positive database id (JSON number or decimal string) as its decimal string, or null (spec 7.1 format). */
function idString(value) {
  if (typeof value === 'number') return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
  return typeof value === 'string' && FORMAT_RE.id.test(value) ? value : null;
}

/** A catalog ROM filename safe to carry in a query value: bounded, no control characters. */
function validRomFilename(name) {
  if (typeof name !== 'string' || name.length === 0 || name.length > ROM_FILENAME_MAX) return false;
  for (const ch of name) {
    const code = ch.codePointAt(0);
    if (code < 0x20 || code === 0x7f) return false;
  }
  return true;
}

/** Open in game and Edit: the same-origin solo launch URL with PR 2's parameters, each checked; null (no link) when one fails. */
export function authoringLaunchUrl({ systemId, romFilename = null, romHash = null, itemId, checkpointFileId = null }) {
  if (!AUTHORING_SYSTEMS.includes(systemId)) return null;
  const item = idString(itemId);
  const checkpoint = checkpointFileId == null ? null : idString(checkpointFileId);
  if (!item || (checkpointFileId != null && !checkpoint)) return null;
  const params = new URLSearchParams();
  params.set('system', systemId);
  if (validRomFilename(romFilename)) {
    params.set('rom', romFilename);
    params.set('solo', '1');
  } else if (typeof romHash === 'string' && FORMAT_RE.romHash.test(romHash)) {
    params.set('myrom', romHash);
  } else {
    return null;
  }
  params.set('author', item);
  if (checkpoint) params.set('author_checkpoint', checkpoint);
  return `/?${params}`;
}

/** An error's `{ error, ...details }` body; text is parsed only within ERROR_BODY_MAX_BYTES characters (a longer one reads as none). */
function errorBody(err) {
  if (err?.body && typeof err.body === 'object') return err.body;
  if (typeof err?.body === 'string' && err.body && err.body.length <= ERROR_BODY_MAX_BYTES) {
    try { return JSON.parse(err.body); } catch { return null; }
  }
  return null;
}

/** One classification for every authoring failure (spec 7.3; status 0 is a network error or a client refusal), `cause` kept. */
export function classifyError(err) {
  const body = errorBody(err);
  const status = Number.isInteger(err?.status) ? err.status : 0;
  const code = typeof body?.error === 'string' ? body.error : null;
  return { status, code, body, retryClass: classifyAuthoringResponse({ status, code }), cause: err };
}

/**
 * A mutation failure whose outcome nobody settled (L1-2): the server could not confirm its commit
 * (`commit: 'unknown'`), a 5xx that promises no rollback, or a network failure once the request may
 * have been sent. Only a 503 `deadline_exceeded` without `commit: 'unknown'` (spec 7.1: missed its
 * deadline before committing, so rolled back) and a catalogued 5xx refusal changed nothing.
 */
export function outcomeUnknown({ status = 0, code = null, body = null } = {}) {
  if (body?.commit === 'unknown') return true;
  if (status === 0) return code === null;   // a client refusal carries its own code; a network failure has none
  if (status < 500) return false;
  if (code === 'deadline_exceeded' && status === 503) return false;
  return own(AUTHORING_ERROR_CODES, code)?.status !== status;
}

/** quota_exceeded by its details: the cap's message with its limit (a malformed limit reads the shared one), else the account's; null when neither is known. */
function quotaMessage(body) {
  const cap = own(QUOTA_CAP_MESSAGES, body?.cap);
  if (cap) {
    const [fallback, message] = cap;
    return message(Number.isSafeInteger(body?.limit) && body.limit > 0 ? body.limit : fallback);
  }
  return own(QUOTA_ACCOUNT_MESSAGES, body?.account) ?? null;
}

export function errorMessage({ status = 0, code = null, body = null, timedOut = false, unknownOutcome = false } = {}) {
  if (timedOut) return TIMEOUT_MESSAGE;
  if (unknownOutcome) return UNKNOWN_OUTCOME_MESSAGE;
  // Task 17 (display_seq on publish) and the item, version, check, label and evidence caps.
  if (code === 'quota_exceeded') {
    const quota = quotaMessage(body);
    if (quota) return quota;
  }
  if (code === 'not_ready') {
    const failed = Array.isArray(body?.failed) ? body.failed : (Array.isArray(body?.details?.failed) ? body.details.failed : []);
    const reasons = failed.map((reason) => own(NOT_READY_REASONS, reason)).filter(Boolean);
    return reasons.length ? `This version is not ready to publish: ${reasons.join(', ')}.` : 'This version is not ready to publish.';
  }
  const known = own(ERROR_MESSAGES, code) || own(CLIENT_ERROR_MESSAGES, code) || own(ACCOUNT_ERROR_MESSAGES, code);
  if (known) return known;
  if (status === 401) return ACCOUNT_ERROR_MESSAGES.unauthorized;
  if (status === 0) return 'Could not reach the server. Check your connection and try again.';
  if (status >= 500) return 'The server could not finish that. Try again in a moment.';
  return 'That did not work. Reload the page and try again.';
}

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/** A 2xx answer that failed validation: a typed, terminal client-side outcome. */
const responseInvalid = () => ({ ok: false, status: 0, code: 'response_invalid', body: null, retryClass: 'terminal', cause: null });

// Field checks for every answer (the Interfaces' Consumes block): each listed field present, at its type and bounds.
const isText = (value) => typeof value === 'string' && value.length <= TEXT_MAX;
const nullOr = (check) => (value) => value === null || check(value);
const oneOf = (list) => (value) => list.includes(value);
const isCount = (value) => Number.isSafeInteger(value) && value >= 1;
const isBool = (value) => typeof value === 'boolean';
const isId = (value) => idString(value) !== null;
const isTime = (value) => Number.isFinite(value) && value >= 0;
const isScore = (value) => Number.isSafeInteger(value) && value >= 0 && value <= PUBLISH.scoreMax;
/** `row` is an object holding every field of `fields`, each passing its check (which also gets the row). */
const validRow = (fields) => (row) => isPlainObject(row) && Object.entries(fields).every(([field, check]) => Object.hasOwn(row, field) && check(row[field], row));

/**
 * A parsed JSON value within `max` values, keys and 32-character runs of text, nested at most
 * JSON_DEPTH_MAX deep, and whose JSON text is at most `encodedMax` characters long: walked in place
 * (no copy), stopping at the first step past a bound. The length counted is a lower bound of the
 * encoding (strings and keys unescaped, every number one character), so it never refuses a value
 * whose encoding fits.
 */
function jsonWithin(value, max, encodedMax = Infinity) {
  let budget = max;
  let encoded = 0;
  const fits = (chars) => {
    encoded += chars;
    return encoded <= encodedMax;
  };
  const walk = (node, depth) => {
    budget -= 1;
    if (budget < 0 || depth > JSON_DEPTH_MAX) return false;
    if (typeof node === 'string') {
      budget -= Math.floor(node.length / 32);
      return budget >= 0 && fits(node.length + 2);
    }
    if (node === null || typeof node === 'boolean') return fits(node === false ? 5 : 4);
    if (typeof node === 'number') return fits(1);
    if (Array.isArray(node)) return fits(1 + Math.max(1, node.length)) && node.every((child) => walk(child, depth + 1));
    if (!isPlainObject(node)) return false;
    if (!fits(2)) return false;
    let first = true;
    for (const key in node) {
      if (!Object.hasOwn(node, key)) continue;
      budget -= 1 + Math.floor(key.length / 32);
      // "key": plus the comma before every member but the first
      if (budget < 0 || !fits(key.length + (first ? 3 : 4)) || !walk(node[key], depth + 1)) return false;
      first = false;
    }
    return true;
  };
  return walk(value, 0);
}

const ITEM_FIELDS = {
  id: isId, game_id: isId, game_title: nullOr(isText), system_id: oneOf(AUTHORING_SYSTEMS), kind: oneOf(ITEM_KINDS),
  status: oneOf(ITEM_STATUSES), row_version: Number.isSafeInteger, current_version_id: nullOr(isId),
  // Spec 6.3: a version number exactly when its pointer; a draft has no published version, any other status has one.
  current_version_no: (no, item) => (item.current_version_id === null ? no === null : isCount(no)),
  published_version_id: (id, item) => (item.status === 'draft' ? id === null : isId(id)),
  published_version_no: (no, item) => (item.published_version_id === null ? no === null : isCount(no)),
  name: nullOr(isText), created_by_name: nullOr(isText),
  last_test_at: nullOr(isText), ever_published: isBool, ready: isBool, check_state: oneOf(Object.keys(CHECK_LABELS)),
  failed: (value) => Array.isArray(value) && value.length <= 32 && value.every(isText), needs_retest: isBool, superseded: isBool,
};
// Spec 6.3 version content: points 1 to 100, payload_bytes 1 to LIMITS.payloadBytesMax, definitions within their structure bound.
const VERSION_FIELDS = {
  id: isId, version_no: isCount, name: nullOr(isText), description: nullOr(isText), icon: nullOr(isText),
  points: nullOr((value) => Number.isSafeInteger(value) && value >= PUBLISH.pointsMin && value <= PUBLISH.pointsMax),
  event_type: oneOf(EVENT_TYPES), trigger: nullOr(isPlainObject), score_spec: nullOr(isPlainObject), leaderboard_gate: nullOr(isPlainObject),
  dsl_version: isCount, payload_bytes: (value) => Number.isSafeInteger(value) && value >= 1 && value <= LIMITS.payloadBytesMax,
  manual_edit: isBool, created_by_name: nullOr(isText), created_at: isText,
};
/**
 * A version row of `item` (spec 6.3): its fields, its definitions within their structure bound and
 * within its own payload_bytes (the size of the serialized delivery entry they are part of, so their
 * JSON text can never be longer; the array around them takes less than the entry's other fields),
 * and the shape the item's kind requires.
 */
function validVersion(row, item) {
  if (!validRow(VERSION_FIELDS)(row)
    || !jsonWithin([row.trigger, row.score_spec, row.leaderboard_gate], DEFINITION_ELEMENTS_MAX, row.payload_bytes)) return false;
  if (item?.kind === 'achievement') {
    return row.trigger !== null && row.name !== null && row.points !== null && row.event_type !== 'score'
      && row.score_spec === null && row.leaderboard_gate === null;
  }
  // A score version: its definition and no trigger; a C64 score always has its gate.
  return item?.kind === 'score' && row.score_spec !== null && row.trigger === null && row.event_type === 'score'
    && (item.system_id !== 'c64' || row.leaderboard_gate !== null);
}
// An ok outcome carries no reason; a failed one carries one from spec 10.5's list.
const outcomeReason = (reason, entry) => (entry.ok === true ? reason === null : CHECK_REASONS.includes(reason));
const validPerMark = validRow({
  markId: isId, role: oneOf(MARK_ROLES),
  polarity: (polarity, entry) => POLARITIES.includes(polarity) && (entry.role === 'event' || polarity === 'positive'),
  expectedT: isTime, firedT: nullOr(isTime), decodedScore: nullOr(isScore), ok: isBool, reason: outcomeReason,
});
const validGateEntry = validRow({ ok: isBool, reason: outcomeReason });
/**
 * A check row's result (spec 10.5), complete and agreeing with its row: every field present, every mark
 * entry complete and listed once (at most LIMITS.marksActivePerItem), the gate null or its three entries,
 * the row's engine version, and `passed` exactly what the entries say: a non-empty perMark all ok, and a
 * gate (when there is one) whose start and submit passed, and cancel too when present.
 */
function validCheckResult(result, row) {
  if (!isPlainObject(result) || !CHECK_RESULT_FIELDS.every((field) => Object.hasOwn(result, field))) return false;
  const { passed, engineVersion, perMark, gate } = result;
  if (typeof passed !== 'boolean' || passed !== row.passed || !isText(engineVersion) || engineVersion !== row.engine_version) return false;
  if (!Array.isArray(perMark) || perMark.length > LIMITS.marksActivePerItem || !perMark.every(validPerMark)) return false;
  if (new Set(perMark.map((entry) => idString(entry.markId))).size !== perMark.length) return false;
  let gatePassed = true;
  if (gate !== null) {
    if (!isPlainObject(gate) || !GATE_ROLES.every((role) => Object.hasOwn(gate, role) && (gate[role] === null || validGateEntry(gate[role])))) return false;
    gatePassed = gate.start !== null && gate.submit !== null && GATE_ROLES.every((role) => gate[role] === null || gate[role].ok === true);
  }
  return passed === (perMark.length > 0 && perMark.every((entry) => entry.ok === true) && gatePassed);
}
const ROW_FIELDS = {
  items: ITEM_FIELDS,
  checks: { passed: isBool, engine_version: isText, created_at: isText, current: isBool, result: validCheckResult },
  tests: {
    created_at: isText, version_no: nullOr(isCount), rom_hash: isText, core_name: isText, outcome: oneOf(TEST_OUTCOMES),
    // Spec 6.3: a score run, and only a score run, has a peak score; a reported miss is a confirmed verdict.
    peak_score: (score, run) => (run.outcome === 'score' ? isScore(score) : score === null),
    verdict: (verdict, run) => VERDICTS.includes(verdict) && (run.outcome !== 'missed' || verdict === 'confirmed'),
    matched_screen: nullOr(isBool), tester_name: nullOr(isText),
  },
  audit: {
    created_at: isText, action: oneOf(AUDIT_ACTIONS), actor_kind: oneOf(ACTOR_KINDS), actor_name: nullOr(isText),
    version_id: nullOr(isId), details: nullOr((details) => isPlainObject(details) && jsonWithin(details, DETAILS_ELEMENTS_MAX)),
  },
};
const validDetailItem = validRow({ ...ITEM_FIELDS, launch_rom: nullOr(validRow({ rom_hash: isText, rom_filename: nullOr(isText) })) });
const validFile = validRow({
  // A slot with no stored file is absent; every declared, attached or tombstoned file has its id (an Open in game link needs it).
  id: (id, file) => (file.state === 'absent' ? id === null || isId(id) : isId(id)),
  kind: oneOf(FILE_KINDS), state: oneOf(FILE_STATES),
  unusable_at: (value, file) => value === null || (isText(value) && (file.kind === 'checkpoint_near' || file.kind === 'checkpoint_early')),
});
/** A mark's files (spec 6.3): at most one per kind, never test frames, and the memory recording never absent. */
function validMarkFiles(files) {
  if (!Array.isArray(files) || files.length > MARK_FILES_MAX || !files.every(validFile)) return false;
  const kinds = new Set(files.map((file) => file.kind));
  return kinds.size === files.length && !kinds.has('test_frames') && files.every((file) => file.kind !== 'memory' || file.state !== 'absent');
}
/** A mark of `item` (spec 6.3): event marks only on achievement items, a typed score exactly on score readings, only events negative. */
const validMark = (item) => validRow({
  id: isId, role: (role) => MARK_ROLES.includes(role) && (role === 'event') === (item.kind === 'achievement'),
  polarity: (polarity, mark) => POLARITIES.includes(polarity) && (mark.role === 'event' || polarity === 'positive'),
  event_type: nullOr(isText), rom_hash: isText, rom_filename: nullOr(isText), core_name: isText, event_t_ms: Number.isFinite,
  typed_score: (score, mark) => (mark.role === 'score_reading' ? isScore(score) : score === null),
  abandoned_at: nullOr(isText), published: isBool, files: validMarkFiles,
});

/**
 * A page answer (Task 14), checked before it replaces any state: at most `max` valid rows (version rows
 * against `item`, the detail's validated item), a cursor exactly when `has_more`. Else null.
 */
function validPage(value, name, max, item = null) {
  const rows = value?.[name];
  const validOne = name === 'versions' ? (row) => validVersion(row, item) : validRow(ROW_FIELDS[name]);
  if (!isPlainObject(value) || !Array.isArray(rows) || rows.length > max || !rows.every(validOne)) return null;
  const { next_cursor: cursor, has_more: hasMore } = value;
  const consistent = hasMore === true
    ? typeof cursor === 'string' && cursor.length > 0 && cursor.length <= CURSOR_MAX
    : hasMore === false && cursor === null;
  return consistent ? { rows, nextCursor: cursor, hasMore } : null;
}

/** A detail snapshot matches its item's pointer: null exactly when the pointer is, else that version of the item (same id and number). */
const snapshotMatches = (snapshot, id, no, item) => (id === null ? snapshot === null
  : validVersion(snapshot, item) && idString(snapshot.id) === idString(id) && snapshot.version_no === no);

/** The detail answer (Task 14) for item `id`, checked before it replaces the screen: every field it reads, bounded, consistent, both snapshots matching. */
function validDetail(value, id) {
  const item = value?.item;
  return isPlainObject(value) && validDetailItem(item) && idString(item.id) === id
    && snapshotMatches(value.current_version, item.current_version_id, item.current_version_no, item)
    && snapshotMatches(value.published_version, item.published_version_id, item.published_version_no, item)
    && Array.isArray(value.marks) && value.marks.length <= LIMITS.marksTotalPerItem && value.marks.every(validMark(item));
}

/** Run a rendering step; a throw is kept as `render_failed` with its cause and reported as false. */
function rendered(part, render) {
  try {
    render();
    return true;
  } catch (error) {
    noteDiagnostic(part, 'render_failed', error);
    return false;
  }
}

/** A publish, unpublish or delete answer (Task 17): `committed` only for an explicit true; every failed, missing or malformed refresh part listed. */
export function publicationResult(value) {
  const committed = isPlainObject(value) && value.committed === true;
  const refresh = isPlainObject(value?.refresh) ? value.refresh : {};
  const refreshFailures = [];
  for (const part of REFRESH_PARTS) {
    const entry = refresh[part];
    if (isPlainObject(entry) && entry.status === 'ok') continue;
    if (isPlainObject(entry) && entry.status === 'failed') {
      refreshFailures.push({ part, code: typeof entry.code === 'string' && REFRESH_CODE_RE.test(entry.code) ? entry.code : 'failed' });
    } else {
      refreshFailures.push({ part, code: 'not_reported' });
    }
  }
  return { committed, refreshFailures };
}

/** The spec 7.5 refresh report, as one sentence when any part failed or was not reported. */
export function refreshSummary(response) {
  const { refreshFailures } = publicationResult(response);
  if (!refreshFailures.length) return '';
  const parts = refreshFailures.map(({ part, code }) => `${REFRESH_LABELS[part]} (${code === 'not_reported' ? 'not reported' : code})`);
  return `Some refreshes did not finish on this server: ${parts.join(', ')}. Other servers will catch up within their 60-second fallback refresh.`;
}

const joinSentences = (...parts) => parts.filter(Boolean).join(' ');
const versionLabel = (no) => (no == null ? '–' : `Version ${no}`);
const formatSeconds = (ms) => (Number.isFinite(Number(ms)) ? `${(Number(ms) / 1000).toFixed(3)} s` : '–');

function statusPill(status) {
  const tone = STATUS_TONES[status.key];
  return `<span class="pill${tone ? ` ${tone}` : ''}">${esc(status.label)}</span>`;
}

function fieldText(value) {
  if (value === null || value === undefined || value === '') return '–';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

export function compareVersions(left, right) {
  return COMPARE_FIELDS.map(([key, label]) => {
    const l = left ? fieldText(left[key]) : '–';
    const r = right ? fieldText(right[key]) : '–';
    return { key, label, left: l, right: r, changed: l !== r };
  });
}

/** Frames for the flipbook, from Task 4's decodeFrames (it validates the whole file). */
export function flipbookFrames(bytes) {
  const { header, images } = decodeFrames(bytes);
  return header.frames.map((frame, index) => ({
    t: frame.t, sampleIndex: frame.sampleIndex ?? null, jpeg: images[index],
  }));
}

/** The outcome of a request whose operation ended: by its deadline (timedOut) or by navigation. */
function abortedOutcome(signal, { timedOut } = {}) {
  return {
    ok: false, aborted: true, timedOut: timedOut ?? signal?.reason?.name === 'TimeoutError',
    status: 0, code: null, body: null, retryClass: null,
  };
}

const diagnostics = [];   // { entry, bytes }, oldest first
let diagnosticBytes = 0;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/**
 * At most `max` UTF-16 units of `value` (never ending inside a surrogate pair) as an independently
 * allocated string: a slice may share its parent's storage (V8 keeps a sliced string as a view of
 * the original), so the cut is copied through UTF-8 and the oversized original is never retained.
 */
function boundedCopy(value, max) {
  let end = Math.min(value.length, max);
  if (end > 0 && end < value.length) {
    const last = value.charCodeAt(end - 1);
    if (last >= 0xd800 && last <= 0xdbff) end -= 1;
  }
  return end === 0 ? '' : textDecoder.decode(textEncoder.encode(value.slice(0, end)));
}

/**
 * A failure's cause as a bounded, frozen record of plain values, never the error itself (which may
 * hold a response body, buffers or any other graph): its name, message, code (text or number),
 * status, at most ERROR_BODY_MAX_BYTES of a text body (an object body is not kept), a bounded stack,
 * its causes to DIAGNOSTIC_DEPTH_MAX below it, and at most DIAGNOSTIC_ERRORS_MAX of an
 * AggregateError's errors (`causeDropped` and `errorsDropped` say what was left out). Every read is
 * guarded (a throwing getter reads as absent), a cycle is marked rather than followed, and one entry
 * keeps at most DIAGNOSTIC_TEXT_MAX characters in DIAGNOSTIC_RECORDS_MAX records. Returns
 * `{ record, bytes }`, `bytes` being what the record retains.
 */
function diagnosticRecord(cause) {
  let text = DIAGNOSTIC_TEXT_MAX;
  let records = 0;
  const seen = new Set();
  const read = (object, key) => {
    try {
      return object[key];
    } catch {
      return undefined;
    }
  };
  const keep = (value, max) => {
    if (typeof value !== 'string' || text <= 0) return undefined;
    const kept = boundedCopy(value, Math.min(max, text));   // its own allocation (boundedCopy)
    text -= kept.length;
    return kept;
  };
  const put = (out, key, value) => {
    if (value !== undefined) out[key] = value;
  };
  const record = (value, depth) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object' || typeof value === 'function') {
      if (seen.has(value)) return Object.freeze({ cycle: true });
      seen.add(value);
    }
    records += 1;
    const out = {};
    if (typeof value !== 'object' && typeof value !== 'function') {
      let message;
      try {
        message = String(value);
      } catch {
        message = undefined;
      }
      put(out, 'message', keep(message, DIAGNOSTIC_FIELD_MAX));
      return Object.freeze(out);
    }
    put(out, 'name', keep(read(value, 'name'), DIAGNOSTIC_FIELD_MAX));
    put(out, 'message', keep(read(value, 'message'), DIAGNOSTIC_FIELD_MAX));
    const code = read(value, 'code');
    put(out, 'code', Number.isFinite(code) ? code : keep(code, DIAGNOSTIC_FIELD_MAX));
    const status = read(value, 'status');
    if (Number.isSafeInteger(status)) out.status = status;
    put(out, 'body', keep(read(value, 'body'), ERROR_BODY_MAX_BYTES));
    put(out, 'stack', keep(read(value, 'stack'), DIAGNOSTIC_FIELD_MAX));
    const room = () => depth < DIAGNOSTIC_DEPTH_MAX && records < DIAGNOSTIC_RECORDS_MAX;
    const inner = read(value, 'cause');
    if (inner !== undefined && inner !== null) {
      if (room()) out.cause = record(inner, depth + 1);
      else out.causeDropped = true;
    }
    const errors = read(value, 'errors');
    let length = -1;
    try {
      if (Array.isArray(errors)) length = errors.length;
    } catch {
      length = -1;   // a revoked proxy, or a length that refuses to be read
    }
    if (Number.isSafeInteger(length) && length >= 0) {
      const kept = [];
      for (let i = 0; i < length && kept.length < DIAGNOSTIC_ERRORS_MAX && room(); i += 1) kept.push(record(read(errors, i), depth + 1));
      out.errors = Object.freeze(kept);
      if (length > kept.length) out.errorsDropped = length - kept.length;
    }
    return Object.freeze(out);
  };
  let result;
  try {
    result = record(cause, 0);
  } catch {
    result = Object.freeze({ unreadable: true });   // a proxy or host object that refused to be read
  }
  return { record: result, bytes: 2 * (DIAGNOSTIC_TEXT_MAX - text) + DIAGNOSTIC_RECORD_BYTES * Math.max(1, records) };
}

/**
 * Keep a failure for diagnosis: `{ part, code, cause }` with `cause` as a bounded record
 * (diagnosticRecord). At most DIAGNOSTICS_MAX entries and DIAGNOSTICS_BYTES_MAX retained bytes: the
 * oldest entries go first. Messages shown to the admin come from the code, never from here.
 */
function noteDiagnostic(part, code, cause) {
  const { record, bytes } = diagnosticRecord(cause ?? null);
  const kept = typeof code === 'string' ? boundedCopy(code, DIAGNOSTIC_FIELD_MAX) : null;
  const entry = Object.freeze({ part, code: kept, cause: record });
  const size = bytes + 2 * (String(part).length + (kept?.length ?? 0));
  diagnostics.push({ entry, bytes: size });
  diagnosticBytes += size;
  while (diagnostics.length > DIAGNOSTICS_MAX || (diagnosticBytes > DIAGNOSTICS_BYTES_MAX && diagnostics.length > 1)) {
    diagnosticBytes -= diagnostics.shift().bytes;
  }
}

/** The last failures kept (noteDiagnostic), oldest first, each a frozen `{ part, code, cause }`. */
export function authoringDiagnostics() {
  return diagnostics.map(({ entry }) => entry);
}

/** Wait before a retry; false (and the timer cleared) when the signal aborts first. */
function waitForRetry(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(false);
      return;
    }
    let timer = null;
    const onAbort = () => {
      clearTimeout(timer);
      resolve(false);
    };
    timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve(true);
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Await `promise` while the signal is live; an abort ends the wait at once (for dependencies that ignore it, such as the step-up dialog). */
async function untilAborted(promise, signal) {
  if (!signal) return promise;
  const abort = whenAborted(signal);
  try {
    return await Promise.race([promise, abort.promise]);
  } finally {
    abort.cancel();
  }
}

/**
 * One authoring request with the shared retry classes (`reauth` steps up and
 * resends once, `retryable` backs off up to delays.length times), raced against
 * `signal` and fenced on `deadlineAt` as the Interfaces say. A `mutation` whose
 * outcome is unknown (outcomeUnknown) is never resent: it answers
 * `{ ok: false, unknownOutcome: true, ... }` so the screen reloads what the server holds.
 */
export async function sendAuthoringRequest(send, {
  stepUp = ensureRecentAdminAuth, delays = RETRY_DELAYS_MS, signal = null, deadlineAt = null, mutation = false,
} = {}) {
  let steppedUp = false;
  let retries = 0;
  const ended = () => {
    if (signal?.aborted) return abortedOutcome(signal);
    if (Number.isFinite(deadlineAt) && performance.now() >= deadlineAt) return abortedOutcome(signal, { timedOut: true });
    return null;
  };
  for (;;) {
    const before = ended();
    if (before) return before;
    try {
      // Nothing starts on an aborted signal: the check runs right before `send`.
      const value = await untilAborted(Promise.resolve().then(() => {
        if (signal?.aborted) throw signal.reason;
        return send(signal);
      }), signal);
      // Fence a late success: the operation already ended (deadline or navigation).
      return ended() ?? { ok: true, value };
    } catch (err) {
      const stopped = ended();
      if (stopped) return stopped;
      const info = classifyError(err);
      // Only admin_step_up_required is cleared by the step-up sign-in (as runSensitiveAdminMutation does).
      // Every other reauth answer (a missing legal acknowledgement, 401 unauthorized, 403 forbidden) stops
      // here with its own message: a sign-in prompt could not fix it.
      if (info.retryClass === 'reauth' && info.code === 'admin_step_up_required' && !steppedUp) {
        steppedUp = true;
        let confirmed = false;
        try {
          confirmed = await untilAborted(Promise.resolve().then(() => {
            if (signal?.aborted) throw signal.reason;
            return stepUp();
          }), signal);
        } catch (stepUpError) {
          const abandoned = ended();
          if (abandoned) return abandoned;
          // The sign-in check itself failed: not a cancellation, and not the earlier HTTP error.
          return {
            ok: false, stepUpFailed: true, status: 0, code: 'step_up_failed', body: null, retryClass: 'terminal', cause: stepUpError,
          };
        }
        const afterStepUp = ended();
        if (afterStepUp) return afterStepUp;
        if (!confirmed) return { ok: false, cancelled: true, ...info };
        continue;
      }
      if (mutation && outcomeUnknown(info)) return { ok: false, unknownOutcome: true, ...info };
      if (info.retryClass === 'retryable' && retries < delays.length) {
        if (!(await waitForRetry(delays[retries], signal))) return abortedOutcome(signal);
        retries += 1;
        continue;
      }
      return { ok: false, ...info };
    }
  }
}

const tooLarge = () => new ApiError(413, JSON.stringify({ error: 'evidence_too_large' }));
// Client-side refusals, as 400s so classifyAuthoringResponse treats them as terminal (status 0 would be retryable).
const streamUnsupported = () => new ApiError(400, JSON.stringify({ error: 'stream_unsupported' }));
const streamStalled = () => new ApiError(400, JSON.stringify({ error: 'stream_stalled' }));
// The operation's absolute deadline passed while reading; sendAuthoringRequest
// reports it as timed out (its deadlineAt check), never as a retryable error.
const readDeadlinePassed = () => new DOMException('deadline exceeded', 'TimeoutError');

/** Give timers (the operation's deadline and abort) a chance to run during a long read. */
function yieldToTimers() {
  return new Promise((resolve) => { setTimeout(resolve, 0); });
}

/** One download's retained-byte account: every kept allocation is charged before it is made. */
class ByteBudget {
  constructor(limit) {
    this.limit = limit;
    this.used = 0;
  }

  charge(bytes) {
    if (this.used + bytes > this.limit) return false;
    this.used += bytes;
    return true;
  }

  release(bytes) {
    this.used = Math.max(0, this.used - bytes);
  }
}

/** A cancel's typed outcome (`settled`, `rejected` with its cause, `timeout`), waited at most CANCEL_WAIT_MAX_MS and never past `deadlineAt`. */
async function settleWithin(promise, deadlineAt) {
  const waitMs = Math.max(0, Math.min(CANCEL_WAIT_MAX_MS, deadlineAt - performance.now()));
  let timer = null;
  try {
    return await Promise.race([
      Promise.resolve(promise).then(() => ({ status: 'settled' }), (cause) => ({ status: 'rejected', cause })),
      new Promise((resolve) => { timer = setTimeout(() => resolve({ status: 'timeout' }), waitMs); }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/** Cancel a stream reader, with `threw` when cancel() throws; unless it settled, `abortRequest` stops the transfer. */
async function cancelReader(reader, deadlineAt, abortRequest = null) {
  let outcome;
  try {
    outcome = await settleWithin(reader.cancel(), deadlineAt);
  } catch (cause) {
    outcome = { status: 'threw', cause };
  }
  if (outcome.status !== 'settled') abortRequest?.();
  return outcome;
}

/** Keep a cancel that did not settle for diagnosis, beside the original failure (one AggregateError); the original travels on unchanged. */
function noteCancelFailure(original, outcome) {
  if (outcome.status === 'settled') return;
  const code = `cancel_${outcome.status}`;
  const cancel = Object.assign(new Error(`body cancel ${outcome.status}`, outcome.cause === undefined ? undefined : { cause: outcome.cause }), { code });
  noteDiagnostic('body_cancel', code, new AggregateError([original, cancel], `${original?.message || 'read stopped'}; ${cancel.message}`));
}

/**
 * Read a body within `budget` into one owned doubling buffer (each growth
 * charged first, old and new both counted, a reservation owned until its
 * allocation and copy succeeded), cut to its exact size. `overLimit()` is thrown
 * past `maxBytes` or the budget; the stall, deadline, yield and cancel rules
 * are the Decisions'. A reader-less body is refused and its request aborted.
 */
async function readBodyBounded(res, { maxBytes, budget, deadlineAt, signal = null, overLimit, abortRequest = null }) {
  let reader = null;
  let readerError;
  try {
    reader = res.body?.getReader?.() ?? null;
  } catch (err) {
    readerError = err;
  }
  if (!reader) {
    abortRequest?.();
    const refusal = streamUnsupported();
    if (readerError !== undefined) refusal.cause = readerError;
    throw refusal;
  }
  let buffer = null;
  let charged = 0;
  let pending = 0;   // a reservation whose allocation and copy have not succeeded yet
  let total = 0;
  let empty = 0;
  let lastYield = performance.now();
  try {
    for (;;) {
      if (signal?.aborted) throw signal.reason;
      if (performance.now() >= deadlineAt) throw readDeadlinePassed();
      const { done, value } = await untilAborted(reader.read(), signal);
      if (done) break;
      if (!(value instanceof Uint8Array)) throw streamUnsupported();
      if (value.byteLength === 0) {
        if (++empty > EMPTY_CHUNKS_MAX) throw streamStalled();
      } else {
        const size = value.byteLength;
        if (total + size > maxBytes) throw overLimit();
        if (total + size > charged) {
          const capacity = Math.min(maxBytes, Math.max(total + size, charged * 2));
          if (!budget.charge(capacity)) throw overLimit();
          pending = capacity;
          const next = new Uint8Array(capacity);
          if (buffer) next.set(buffer.subarray(0, total));
          budget.release(charged);
          buffer = next;
          charged = capacity;
          pending = 0;
        }
        buffer.set(value, total);
        total += size;
      }
      if (performance.now() - lastYield >= READ_YIELD_MS) {
        await yieldToTimers();
        lastYield = performance.now();
      }
    }
  } catch (err) {
    budget.release(charged + pending);
    buffer = null;
    noteCancelFailure(err, await cancelReader(reader, deadlineAt, abortRequest));
    throw err;
  }
  if (!buffer) return new Uint8Array(0);
  if (charged === total) return buffer;
  // An exact-size copy; the larger buffer's charge goes either way, the copy's stays only when it exists.
  try {
    if (!budget.charge(total)) throw overLimit();
    pending = total;
    const bytes = buffer.slice(0, total);
    pending = 0;
    return bytes;
  } finally {
    budget.release(charged + pending);
  }
}

/** At most ERROR_BODY_MAX_BYTES of an error body as `{ text, error }` (empty text and the failure kept when unreadable). */
async function readErrorText(res, signal, deadlineAt, abortRequest = null) {
  try {
    const bytes = await readBodyBounded(res, {
      maxBytes: ERROR_BODY_MAX_BYTES, budget: new ByteBudget(2 * ERROR_BODY_MAX_BYTES), deadlineAt, signal,
      overLimit: () => new Error('error body over its limit'), abortRequest,
    });
    return { text: new TextDecoder().decode(bytes), error: null };
  } catch (error) {
    return { text: '', error };
  }
}

/**
 * Download one evidence file within its cap (terminal evidence_too_large past
 * it), on its own controller following the operation's signal; every path but a
 * body read to its end aborts the request before detaching that listener.
 */
async function fetchFileBytes(fileId, signal, deadlineAt, maxBytes = FRAMES_DOWNLOAD_MAX_BYTES) {
  const request = new AbortController();
  const follow = () => request.abort(signal.reason);
  if (signal?.aborted) follow();
  else signal?.addEventListener('abort', follow, { once: true });
  const abortRequest = () => request.abort(new DOMException('body cancel not confirmed', 'AbortError'));
  let readToEnd = false;
  try {
    const res = await fetch(`${API}/files/${encodeURIComponent(fileId)}`, { credentials: 'include', cache: 'no-store', signal: request.signal });
    if (!res.ok) {
      const { text, error } = await readErrorText(res, request.signal, deadlineAt, abortRequest);
      const failure = new ApiError(res.status, text);
      if (error) failure.cause = error;   // why the error body could not be read
      throw failure;
    }
    const rawLength = res.headers?.get?.('Content-Length') ?? null;
    const declared = typeof rawLength === 'string' && CONTENT_LENGTH_RE.test(rawLength) ? Number(rawLength) : null;
    if (declared !== null && declared > maxBytes) {
      const refusal = tooLarge();
      const reader = res.body?.getReader?.();
      if (reader) noteCancelFailure(refusal, await cancelReader(reader, deadlineAt, abortRequest));
      else abortRequest();   // nothing can cancel it: stop the request itself
      throw refusal;
    }
    const bytes = await readBodyBounded(res, {
      maxBytes, budget: new ByteBudget(2 * maxBytes), deadlineAt, signal: request.signal, overLimit: tooLarge, abortRequest,
    });
    readToEnd = true;
    return bytes;
  } finally {
    if (!readToEnd) request.abort(new DOMException('download ended', 'AbortError'));
    signal?.removeEventListener('abort', follow);
  }
}

/** Move focus to `fallback` when re-rendering detached the focused element. */
function restoreFocus(fallback) {
  const active = document.activeElement;
  if (!active || active === document.body || !active.isConnected) fallback?.focus();
}

// ── Dialogs ──

const openDialogs = new Set();
let dialogSeq = 0;

function openDialog({ title, description, bodyHtml = '', confirmLabel, opener }) {
  const titleId = `authoring-dialog-title-${++dialogSeq}`;
  const overlay = document.createElement('div');
  overlay.className = 'admin-security-overlay';
  overlay.innerHTML = `
    <div class="admin-security-dialog" role="dialog" aria-modal="true" aria-labelledby="${titleId}">
      <h2 id="${titleId}"></h2>
      <p class="admin-security-description"></p>
      <div class="admin-security-content">${bodyHtml}
        <div class="admin-security-error" role="alert"></div>
        <div class="admin-security-actions">
          <button type="button" class="btn authoring-dialog-cancel">Cancel</button>
          <button type="button" class="btn btn-primary authoring-dialog-confirm"></button>
        </div>
      </div>
    </div>`;
  overlay.querySelector('h2').textContent = title;
  overlay.querySelector('.admin-security-description').textContent = description;
  const dialogEl = overlay.querySelector('[role="dialog"]');
  const confirm = overlay.querySelector('.authoring-dialog-confirm');
  const cancel = overlay.querySelector('.authoring-dialog-cancel');
  confirm.textContent = confirmLabel;
  let busy = false;
  let blocked = false;
  let closed = false;
  // The fields a busy dialog disabled, to enable again when it is no longer busy.
  let heldFields = [];
  const focusables = () => [...dialogEl.querySelectorAll('button, input, select, textarea, a[href]')]
    .filter((el) => !el.disabled && !el.closest('[hidden]'));
  // Disabling the focused control drops focus to <body>, outside the modal, where Escape and the Tab
  // trap no longer reach (WCAG 2.4.3). The dialog itself holds focus while its controls are disabled,
  // and focus comes back to a control once they are enabled again.
  dialogEl.tabIndex = -1;
  /** Run `change` (which may disable controls); when it disabled the focused one, the dialog takes focus. */
  const keepingFocus = (change) => {
    const focused = document.activeElement;
    change();
    if (focused && focused !== dialogEl && dialogEl.contains(focused) && focused.disabled) dialogEl.focus();
  };
  /**
   * While busy every field is disabled with the buttons (WCAG 2.4.3, 2.1.2): nothing changes under the
   * request in flight, and no control is left for Tab to leave the modal from. A radio group would be
   * one: native Tab skips its unchecked radios, so the trap's last control could be one Tab never
   * reaches. Each field the busy state disabled is enabled again afterwards, its value kept.
   */
  const holdFields = (hold) => {
    if (hold && !heldFields.length) {
      heldFields = [...dialogEl.querySelectorAll('input, select, textarea')].filter((el) => !el.disabled);
      for (const el of heldFields) el.disabled = true;
    } else if (!hold) {
      for (const el of heldFields) el.disabled = false;
      heldFields = [];
    }
  };
  const regainFocus = () => {
    if (closed) return;
    const active = document.activeElement;
    if (active && active !== dialogEl && dialogEl.contains(active)) return;
    const fields = focusables();
    (fields.find((el) => el === confirm) || fields.find((el) => el.tagName !== 'BUTTON') || fields[0] || dialogEl).focus();
  };
  const handle = {
    dialog: dialogEl,
    confirm,
    error: overlay.querySelector('.admin-security-error'),
    close() {
      if (closed) return;
      closed = true;
      overlay.remove();
      openDialogs.delete(handle);
      if (opener?.isConnected) opener.focus();
    },
    setBusy(value) {
      keepingFocus(() => {
        busy = value;
        confirm.disabled = busy || blocked;
        cancel.disabled = busy;
        holdFields(busy);
      });
      if (!busy) regainFocus();
    },
    block(value) {
      keepingFocus(() => {
        blocked = value;
        confirm.disabled = busy || blocked;
      });
    },
  };
  overlay.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !busy) {
      event.preventDefault();
      handle.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusables();
    // Focus stays inside the modal, including while the dialog itself holds it (every button disabled).
    if (!items.length) { event.preventDefault(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (document.activeElement === dialogEl) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
    else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  cancel.addEventListener('click', () => { if (!busy) handle.close(); });
  document.body.appendChild(overlay);
  openDialogs.add(handle);
  (focusables().find((el) => el.tagName !== 'BUTTON') || cancel).focus();
  return handle;
}

function closeDialogs() {
  for (const handle of [...openDialogs]) handle.close();
}

/**
 * One mutation under its own operation (MUTATION_DEADLINE_MS, aborted only by
 * teardown); `apply(outcome, live)` runs inside it, only while its screen generation
 * is current, and calls `live()` after each of its own awaits before it reloads or
 * changes the screen (a teardown can land in any of them). A throw is kept
 * (`continuation_failed`) and shown, never a rejected handler promise. Resolves to
 * apply's result, or null.
 */
async function sendMutation(send, apply, dialog) {
  const gen = detail.gen;
  const live = () => gen === detail.gen;
  const op = startOp(detail, 'mutation', MUTATION_DEADLINE_MS);
  if (!op) {
    // Nothing was sent: the dialog stays open with the reason (startOp kept the cause).
    dialog.setBusy(false);
    dialog.error.textContent = CLIENT_ERROR_MESSAGES.op_start_failed;
    return null;
  }
  try {
    // A success counts only when it completed before the operation's absolute deadline.
    const outcome = await sendAuthoringRequest(send, { signal: op.signal, deadlineAt: op.deadlineAt, mutation: true });
    if (!outcome.ok && !(outcome.aborted && !outcome.timedOut)) {
      noteDiagnostic('mutation', outcome.code ?? (outcome.timedOut ? 'timeout' : `http_${outcome.status}`), outcome.cause ?? null);
    }
    if ((outcome.aborted && !outcome.timedOut) || !live()) return null;
    return await apply(outcome, live);
  } catch (error) {
    noteDiagnostic('mutation', 'continuation_failed', error);
    if (gen === detail.gen) {
      dialog.setBusy(false);
      dialog.error.textContent = CONTINUATION_FAILED_MESSAGE;
      const status = document.getElementById('authoring-detail-status');
      if (status) status.textContent = CONTINUATION_FAILED_MESSAGE;
    }
    return null;
  } finally {
    endOp(detail, op);
  }
}

/** True (noted for diagnosis) when a publish, unpublish or delete answer lacks `committed: true`; synchronous, so nothing yields after the fence. */
function commitUnconfirmed(value) {
  if (publicationResult(value).committed) return false;
  noteDiagnostic('mutation', 'commit_unconfirmed', null);
  return true;
}

/**
 * After a mutation whose outcome is unknown (a client timeout, or a failure outcomeUnknown names):
 * reload the item so the admin sees what the server holds, with `message`. Focus continues from
 * the heading even when the item can no longer be shown (WCAG 2.4.3).
 */
async function reloadAfterUnknown(message) {
  if (await reloadDetail(message) === false) restoreFocus(document.getElementById('authoring-detail-title'));
}

/**
 * The dialog's answer to an unknown outcome (L1-2): true when `outcome` was one, handled by closing and
 * `onUnknown(timedOut)` while `live()` holds (a departed screen is handled by doing nothing).
 */
async function settleUnknown(outcome, dialog, onUnknown, live) {
  if (!outcome.timedOut && !outcome.unknownOutcome) return false;
  if (!live()) return true;
  dialog.close();
  await onUnknown(outcome.timedOut === true);
  return true;
}

const reloadUnknownItem = (timedOut) => reloadAfterUnknown(timedOut ? MUTATION_TIMEOUT_MESSAGE : UNKNOWN_OUTCOME_MESSAGE);

function confirmAndSend({ title, description, confirmLabel, opener, send, onSuccess, onUnknown = reloadUnknownItem, publication = false }) {
  const dialog = openDialog({ title, description, confirmLabel, opener });
  dialog.confirm.addEventListener('click', async () => {
    dialog.error.textContent = '';
    dialog.setBusy(true);
    await sendMutation(send, async (outcome, live) => {
      dialog.setBusy(false);
      if (outcome.ok) {
        dialog.close();
        if (publication && commitUnconfirmed(outcome.value)) {
          await reloadDetail(UNCONFIRMED_MESSAGE);
          return;
        }
        await onSuccess(outcome.value);
        return;
      }
      // The change may have been saved: never resent, and the dialog never invites a retry of stale state.
      if (await settleUnknown(outcome, dialog, onUnknown, live)) return;
      // A teardown during that await (it yields even when it answers false) ends the continuation here.
      if (!live()) return;
      if (outcome.cancelled) {
        dialog.error.textContent = 'This needs a recent sign-in. Nothing was changed.';
        return;
      }
      if (outcome.retryClass === 'stale' || outcome.retryClass === 'completed') {
        dialog.close();
        await reloadDetail(errorMessage(outcome));
        return;
      }
      dialog.error.textContent = errorMessage(outcome);
    }, dialog);
  });
  return dialog;
}

/** Spec 7.5: a score publish of a version other than the published one needs keep or clear. */
function needsLeaderboardChoice(item, version) {
  return item.kind === 'score' && item.published_version_id != null && version != null
    && String(item.published_version_id) !== String(version.id);
}

/** The item's Ready fields describe its current version only (Task 16): that version is known not to be Ready. */
const knownNotReady = (item, version) => String(version?.id) === String(item.current_version_id) && item.ready !== true;

/** Versions Publish can offer (spec 7.5: any Ready version, the published one included): all but a current version known not to be Ready. */
const publishableVersions = (item) => versionChoices().filter((version) => !knownNotReady(item, version));

/** Publish or republish a chosen version (spec 7.5; the Decisions of Task 19 describe the offer and the leaderboard choice). */
function openPublishDialog(item, opener) {
  const choices = versionChoices();
  const byId = (id) => (id == null ? null : choices.find((v) => String(v.id) === String(id)) || null);
  const offerable = (v) => (v && !knownNotReady(item, v) ? v : null);
  let chosen = offerable(byId(item.current_version_id)) || offerable(byId(item.published_version_id))
    || publishableVersions(item)[0] || null;
  let serverAsked = false;   // the server asked for a leaderboard choice for the chosen version
  let choiceShown = needsLeaderboardChoice(item, chosen);
  const gameTitle = item.game_title || '';
  const option = (v) => `<option value="${esc(v.id)}"${v === chosen ? ' selected' : ''}>${esc(versionOptionLabel(v, item))}</option>`;
  const dialog = openDialog({
    title: `Publish ${itemPhrase(item)}`,
    description: 'Publish the chosen version for players on its confirmed releases and cores. New game loads pick it up within a few minutes.',
    confirmLabel: 'Publish',
    opener,
    bodyHtml: `
      <label class="admin-security-label" for="authoring-publish-version">Version to publish</label>
      <select id="authoring-publish-version" class="settings-input settings-select">${choices.map(option).join('')}</select>
      <p class="authoring-muted" id="authoring-publish-note"></p>
      <fieldset class="authoring-choice" id="authoring-leaderboard-choice"${choiceShown ? '' : ' hidden'}>
        <legend>This changes the published score definition. What happens to the leaderboard?</legend>
        <label><input type="radio" name="authoring-leaderboard" value="keep"> Keep the leaderboard</label>
        <label><input type="radio" name="authoring-leaderboard" value="clear"> Clear the leaderboard</label>
        <div id="authoring-clear-confirm" hidden>
          <label class="admin-security-label" for="authoring-confirm-title">Type the game title to confirm: ${esc(gameTitle)}</label>
          <input id="authoring-confirm-title" type="text" autocomplete="off" spellcheck="false">
        </div>
      </fieldset>`,
  });
  const select = dialog.dialog.querySelector('#authoring-publish-version');
  const note = dialog.dialog.querySelector('#authoring-publish-note');
  const fieldset = dialog.dialog.querySelector('#authoring-leaderboard-choice');
  const clearBox = dialog.dialog.querySelector('#authoring-clear-confirm');
  const titleInput = dialog.dialog.querySelector('#authoring-confirm-title');
  const choice = () => dialog.dialog.querySelector('input[name="authoring-leaderboard"]:checked')?.value || null;
  const sync = () => {
    chosen = byId(select.value);
    choiceShown = serverAsked || needsLeaderboardChoice(item, chosen);
    fieldset.hidden = !choiceShown;
    clearBox.hidden = choice() !== 'clear';
    note.textContent = !chosen ? '' : knownNotReady(item, chosen) ? 'This version is not ready to publish.'
      : String(chosen.id) === String(item.current_version_id) ? 'This version is Ready.'
        : 'The server checks that this version is Ready when you publish it.';
    dialog.block(!chosen || knownNotReady(item, chosen)
      || (choiceShown && (!choice() || (choice() === 'clear' && titleInput.value !== gameTitle))));
  };
  select.addEventListener('change', () => {
    serverAsked = false;
    sync();
  });
  fieldset.addEventListener('change', sync);
  titleInput.addEventListener('input', sync);
  sync();
  dialog.confirm.addEventListener('click', async () => {
    const version = chosen;
    if (!version) return;
    // The version id as the screen read it (a JSON number), with the item's row token.
    const body = { row_version: item.row_version, version_id: version.id };
    if (choiceShown) {
      body.leaderboard = choice();
      if (body.leaderboard === 'clear') body.confirm_title = titleInput.value;
    }
    dialog.error.textContent = '';
    dialog.setBusy(true);
    await sendMutation((signal) => api.post(`${API}/items/${detail.itemId}/publish`, body, { signal }), async (outcome, live) => {
      dialog.setBusy(false);
      if (outcome.ok) {
        dialog.close();
        // Reported as published only with committed: true; a missing or malformed refresh report is a warning.
        if (commitUnconfirmed(outcome.value)) {
          await reloadDetail(UNCONFIRMED_MESSAGE);
          return;
        }
        await reloadDetail(joinSentences(`Published version ${version.version_no}.`, refreshSummary(outcome.value)));
        return;
      }
      // The publication may have committed (L1-2): never resent; the item is reloaded to show what the server holds.
      if (await settleUnknown(outcome, dialog, reloadUnknownItem, live)) return;
      // A teardown during that await (it yields even when it answers false) ends the continuation here.
      if (!live()) return;
      if (outcome.cancelled) {
        dialog.error.textContent = 'Publishing needs a recent sign-in. Nothing was changed.';
        return;
      }
      if (outcome.code === 'leaderboard_choice_required') {
        serverAsked = true;
        sync();
        dialog.error.textContent = errorMessage(outcome);
        fieldset.querySelector('input')?.focus();
        return;
      }
      if (outcome.code === 'confirmation_mismatch') {
        dialog.error.textContent = errorMessage(outcome);
        titleInput.focus();
        return;
      }
      if (outcome.retryClass === 'stale' || outcome.retryClass === 'completed') {
        dialog.close();
        await reloadDetail(errorMessage(outcome));
        return;
      }
      dialog.error.textContent = errorMessage(outcome);
    }, dialog);
  });
}

// ── Operations ──

// Each screen owns the operations its teardown aborts (admin-lifecycle idiom); `deadlineAt`
// (performance.now() ms) lets synchronous work check the deadline without trusting the timer.
// An operation is registered the moment it exists; when finishing its setup throws (a clock that
// fails, say), it is aborted (its timer cleared) and unregistered, the failure kept as
// `op_start_failed` under `part`, and null returned for the caller to report.
function startOp(owner, part, deadlineMs = DEFAULT_OP_DEADLINE_MS) {
  let op = null;
  try {
    op = createOperation(deadlineMs);
    owner.ops.add(op);
    op.deadlineAt = performance.now() + deadlineMs;
    return op;
  } catch (error) {
    if (op) {
      try {
        op.finish();   // clears the timer first, allocating nothing
        op.abort(navAbort());
      } finally {
        owner.ops.delete(op);
      }
    }
    noteDiagnostic(part, 'op_start_failed', error);
    return null;
  }
}

/** True while `op` may still start or finish work: not aborted, not closed, before its deadline. */
function opLive(op) {
  return !op.signal.aborted && performance.now() < op.deadlineAt;
}

/** End `op` after its owned work: abort first (an uncancelled refused request stops too), then clear its timer. */
function endOp(owner, op) {
  op.abort(new DOMException('finished', 'AbortError'));
  op.finish();
  owner.ops.delete(op);
}

function abortOps(owner) {
  for (const op of owner.ops) op.abort(navAbort());
  owner.ops.clear();
}

// ── List ──

const list = {
  bound: false, gen: 0, cursor: null, nextCursor: null, history: [], page: 1, filters: {},
  navigation: createPaginationNavigationGuard(), ops: new Set(), loadOp: null,
  notice: '',   // one message for the next list load (a delete's result and its refresh report)
};

function resetListPagination() {
  list.navigation.reset();
  list.cursor = null;
  list.nextCursor = null;
  list.history = [];
  list.page = 1;
}

function readFilters() {
  const value = (id) => (document.getElementById(id)?.value || '').trim();
  return {
    system: value('authoring-filter-system'),
    kind: value('authoring-filter-kind'),
    status: value('authoring-filter-status'),
    game: value('authoring-filter-game'),
    author: value('authoring-filter-author'),
  };
}

function listRow(item) {
  const id = idString(item.id);
  return `<tr>
    <td>${id ? `<a href="/admin/achievements/${id}">${esc(itemName(item))}</a>` : esc(itemName(item))}</td>
    <td>${esc(item.game_title || `Game ${item.game_id}`)} <span class="authoring-muted">${esc(systemTag(item.system_id))}</span></td>
    <td>${item.kind === 'score' ? 'Score definition' : 'Achievement'}</td>
    <td>${statusPill(displayedStatus(item))}${hasNewerVersion(item) ? ' <span class="pill">Newer version</span>' : ''}</td>
    <td>${esc(versionLabel(item.current_version_no))}</td>
    <td>${esc(item.created_by_name || 'Unknown')}</td>
    <td>${item.last_test_at ? esc(formatDateTime(item.last_test_at)) : 'None yet'}</td>
    <td>${esc(CHECK_LABELS[item.check_state] || CHECK_LABELS.none)}</td>
  </tr>`;
}

async function loadList() {
  const gen = ++list.gen;
  const body = document.getElementById('authoring-list-body');
  const status = document.getElementById('authoring-list-status');
  if (!body || !status) return false;
  const notice = list.notice;
  list.notice = '';
  if (list.filters.game && !idString(list.filters.game)) {
    status.textContent = 'Game ID must be a whole number, for example 42.';
    return false;
  }
  status.textContent = 'Loading…';
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
  for (const [key, value] of Object.entries(list.filters)) if (value) params.set(key, value);
  if (list.cursor) params.set('cursor', list.cursor);
  // A newer load (filters, paging) supersedes this one; teardown aborts it.
  if (list.loadOp) list.loadOp.abort(navAbort());
  const op = startOp(list, 'list');
  list.loadOp = op;
  if (!op) {
    body.innerHTML = '';
    status.textContent = CLIENT_ERROR_MESSAGES.op_start_failed;
    return false;
  }
  // The operation stays open through rendering, which runs only before its absolute deadline.
  try {
    let outcome = await sendAuthoringRequest((signal) => api.get(`${API}/items?${params}`, { signal }),
      { signal: op.signal, deadlineAt: op.deadlineAt });
    if (gen !== list.gen || (outcome.aborted && !outcome.timedOut)) return 'stale';
    // Validated before it replaces anything; a malformed answer clears the rows like a failure.
    const page = outcome.ok ? validPage(outcome.value, 'items', PAGE_SIZE) : null;
    if (outcome.ok && !page) outcome = responseInvalid();
    if (!outcome.ok || !opLive(op)) {
      // A failure, or a valid answer whose validation ended past the deadline (`timeout`, before any rendering).
      noteDiagnostic('list', outcome.ok ? 'timeout' : (outcome.code ?? (outcome.timedOut ? 'timeout' : `http_${outcome.status}`)), outcome.cause ?? null);
      body.innerHTML = '';
      status.textContent = outcome.ok ? TIMEOUT_MESSAGE : outcome.status === 404 && outcome.code === 'not_found'
        ? 'Achievement authoring is turned off. Turn on the achievement_authoring flag in Feature Flags to use this screen.'
        : errorMessage(outcome);
      return false;
    }
    // Rendering is guarded and fenced at completion: rows whose rendering threw
    // or finished after the absolute deadline are taken back, and the cursor and
    // paging controls are committed only after both checks.
    const shown = rendered('list', () => { body.innerHTML = page.rows.map(listRow).join(''); });
    if (!shown || !opLive(op)) {
      body.innerHTML = '';
      status.textContent = shown ? TIMEOUT_MESSAGE : RENDER_FAILED_MESSAGE;
      if (shown) noteDiagnostic('list', 'render_deadline', null);
      return false;
    }
    list.nextCursor = page.nextCursor;
    const control = (id) => document.getElementById(id) || {};
    control('authoring-prev').disabled = list.history.length === 0;
    control('authoring-next').disabled = !page.hasMore;
    control('authoring-page-info').textContent = `Page ${list.page}`;
    status.textContent = joinSentences(notice, page.rows.length ? '' : 'No authored items match these filters.');
    return true;
  } finally {
    endOp(list, op);
    if (list.loadOp === op) list.loadOp = null;
  }
}

function bindList() {
  if (list.bound) return;
  const form = document.getElementById('authoring-filters');
  const prev = document.getElementById('authoring-prev');
  const next = document.getElementById('authoring-next');
  if (!form || !prev || !next) return;
  list.bound = true;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    list.filters = readFilters();
    resetListPagination();
    loadList();
  });
  prev.addEventListener('click', () => list.navigation.run(async () => {
    if (!list.history.length) return;
    const snapshot = { cursor: list.cursor, page: list.page, history: [...list.history] };
    list.cursor = list.history.pop() || null;
    list.page -= 1;
    if (await loadList() === false) Object.assign(list, snapshot);
  }));
  next.addEventListener('click', () => list.navigation.run(async () => {
    if (!list.nextCursor) return;
    const snapshot = { cursor: list.cursor, page: list.page, history: [...list.history] };
    list.history.push(list.cursor);
    // Bounded: Prev reaches back at most LIST_HISTORY_MAX pages; the oldest cursor is dropped.
    if (list.history.length > LIST_HISTORY_MAX) list.history.shift();
    list.cursor = list.nextCursor;
    list.page += 1;
    if (await loadList() === false) Object.assign(list, snapshot);
  }));
}

export function renderList(_params, ctx) {
  bindList();
  ctx?.onTeardown?.(() => {
    list.gen += 1;
    abortOps(list);
    // Release what the screen retained: cursors and rendered rows.
    resetListPagination();
    const body = document.getElementById('authoring-list-body');
    if (body) body.innerHTML = '';
  });
  list.filters = readFilters();
  resetListPagination();
  return loadList();
}

// ── Detail ──

const detail = {
  gen: 0, bound: false, itemId: null, data: null, compare: { left: null, right: null }, flip: null,
  ops: new Set(), loadOp: null,
};

/**
 * A history's state: the rows shown (at most HISTORY_ROWS_MAX), the cursor of the page after them,
 * `skipped` (rows read before the first shown, let go to bound what is kept), and `newest` (the
 * history's first row, which the check section keeps showing as the latest check).
 */
const emptyHistory = (error = null) => ({ rows: [], nextCursor: null, hasMore: false, error, skipped: 0, newest: null, pending: null });

function resetHistories() {
  for (const name of HISTORIES) detail[name] = emptyHistory();
}
resetHistories();

function table(label, headers, rows) {
  // A narrow screen scrolls a table sideways: its named, focusable region lets the keyboard scroll it (WCAG 2.1.1).
  return `<div class="table-scroll" role="region" tabindex="0" aria-label="${esc(label)}"><table class="admin-users-table" aria-label="${esc(label)}">
    <thead><tr>${headers.map((h) => `<th scope="col">${esc(h)}</th>`).join('')}</tr></thead>
    <tbody>${rows}</tbody></table></div>`;
}

function card(title, section, body, moreName = null) {
  // Disabled while that history's page loads ("Show more" and "Show newest" are single-flight per history).
  const history = moreName ? detail[moreName] : null;
  const busy = history?.pending ? ' disabled aria-busy="true"' : '';
  const name = esc(title.toLowerCase());
  const more = history?.hasMore
    ? `<button type="button" class="btn btn-mode" data-action="more" data-section="${moreName}" aria-label="Show more ${name}"${busy}>Show more</button>`
    : '';
  // Rows let go past HISTORY_ROWS_MAX: which entries are shown, and the way back to the first page.
  const skipped = history?.skipped > 0
    ? `<p class="authoring-muted">Showing entries ${history.skipped + 1} to ${history.skipped + history.rows.length}. Newer entries are hidden to keep this page small.</p>
      <button type="button" class="btn btn-mode" data-action="newest" data-section="${moreName}" aria-label="Show newest ${name}"${busy}>Show newest</button>`
    : '';
  // tabindex="-1": "Show more" re-renders the card and returns focus here.
  return `<div class="card admin-card">
    <h3 class="authoring-heading" tabindex="-1">${esc(title)}</h3>
    ${section?.error ? `<p class="authoring-error" role="alert">${esc(section.error)}</p>` : ''}
    ${body}
    ${skipped}
    ${more}
  </div>`;
}

function markLabel(mark) {
  if (mark.role === 'event') return `${POLARITY_LABELS[mark.polarity] || mark.polarity}: ${own(EVENT_LABELS, mark.event_type) || mark.event_type || 'Event'}`;
  if (mark.role === 'score_reading') return `Score reading: ${mark.typed_score}`;
  return ROLE_LABELS[mark.role] || mark.role;
}

function editLaunchUrl() {
  const { item, marks } = detail.data;
  const active = (Array.isArray(marks) ? marks : []).filter((m) => !m.abandoned_at);
  const newest = active[active.length - 1];
  const rom = newest ? { rom_hash: newest.rom_hash, rom_filename: newest.rom_filename } : item.launch_rom;
  if (!rom) return null;
  return authoringLaunchUrl({ systemId: item.system_id, romFilename: rom.rom_filename, romHash: rom.rom_hash, itemId: detail.itemId });
}

function renderSummary() {
  const el = document.getElementById('authoring-section-summary');
  const { item } = detail.data;
  const launch = editLaunchUrl();
  const failed = Array.isArray(item.failed) ? item.failed : [];
  // Ready describes the current version (Task 16); Publish also offers the other versions (spec 7.5).
  const notReady = item.ready !== true && failed.length
    ? `<p class="authoring-muted">${esc(versionLabel(item.current_version_no))} is not ready to publish: ${esc(failed.map((f) => own(NOT_READY_REASONS, f) || f).join(', '))}.</p>`
    : '';
  el.innerHTML = `<div class="card admin-card">
    <h3 class="authoring-heading">Overview</h3>
    <dl class="authoring-facts">
      <div><dt>Game</dt><dd>${esc(item.game_title || `Game ${item.game_id}`)} (${esc(systemTag(item.system_id))})</dd></div>
      <div><dt>Kind</dt><dd>${item.kind === 'score' ? 'Score definition' : 'Achievement'}</dd></div>
      <div><dt>Status</dt><dd>${statusPill(displayedStatus(item))}${hasNewerVersion(item) ? ' <span class="pill">Newer version</span>' : ''}</dd></div>
      <div><dt>Current version</dt><dd>${esc(versionLabel(item.current_version_no))}</dd></div>
      <div><dt>Published version</dt><dd>${esc(versionLabel(item.published_version_no))}</dd></div>
      <div><dt>Author</dt><dd>${esc(item.created_by_name || 'Unknown')}</dd></div>
    </dl>
    ${notReady}
    <div class="authoring-actions">
      <button type="button" class="btn btn-primary" data-action="publish"${publishableVersions(item).length ? '' : ' disabled'}>Publish</button>
      ${item.status === 'published' ? '<button type="button" class="btn" data-action="unpublish">Unpublish</button>' : ''}
      ${launch ? `<a class="btn btn-mode" href="${esc(launch)}">Edit</a>` : ''}
      ${item.ever_published === false ? '<button type="button" class="btn" data-action="delete">Delete</button>' : ''}
    </div>
  </div>`;
}

function versionOptionLabel(version, item) {
  const tags = [];
  if (String(version.id) === String(item.current_version_id)) tags.push('current');
  if (String(version.id) === String(item.published_version_id)) tags.push('published');
  return `Version ${version.version_no}${tags.length ? ` (${tags.join(', ')})` : ''}`;
}

/** Comparable versions: both detail snapshots and every loaded history row, once each by id, newest first. */
function versionChoices() {
  const byId = new Map();
  for (const version of [detail.data?.current_version, detail.data?.published_version, ...detail.versions.rows]) {
    const id = idString(version?.id);
    if (id && !byId.has(id)) byId.set(id, version);
  }
  return [...byId.values()].sort((a, b) => (Number(b.version_no) || 0) - (Number(a.version_no) || 0));
}

function renderVersions() {
  const el = document.getElementById('authoring-section-versions');
  const section = detail.versions;
  const { item } = detail.data;
  const rows = section.rows;
  const choices = versionChoices();
  const byId = (id) => (id == null ? null : choices.find((v) => String(v.id) === String(id)) || null);
  const right = byId(detail.compare.right) || byId(item.current_version_id) || choices[0] || null;
  const publishedOther = item.published_version_id != null && String(item.published_version_id) !== String(right?.id)
    ? item.published_version_id : null;
  const left = byId(detail.compare.left) || byId(publishedOther) || choices.find((v) => v !== right) || null;
  const option = (version, selected) => `<option value="${esc(version.id)}"${selected ? ' selected' : ''}>${esc(versionOptionLabel(version, item))}</option>`;
  const history = rows.map((v) => `<tr>
    <td>${esc(versionOptionLabel(v, item))}</td>
    <td>${esc(v.name || '–')}</td>
    <td>${esc(formatDateTime(v.created_at))}</td>
    <td>${esc(v.created_by_name || 'Unknown')}</td>
    <td>${v.manual_edit ? 'Yes' : 'No'}</td>
    <td>${String(v.id) === String(item.current_version_id) ? '' : `<button type="button" class="btn btn-mode" data-action="restore" data-version-id="${esc(v.id)}" aria-label="Restore version ${esc(v.version_no)}">Restore version</button>`}</td>
  </tr>`).join('');
  const comparison = left && right
    ? `<div class="authoring-filters">
        <label for="authoring-compare-left">Left
          <select id="authoring-compare-left" class="settings-input settings-select" data-compare="left">${choices.map((v) => option(v, v === left)).join('')}</select>
        </label>
        <label for="authoring-compare-right">Right
          <select id="authoring-compare-right" class="settings-input settings-select" data-compare="right">${choices.map((v) => option(v, v === right)).join('')}</select>
        </label>
      </div>
      ${table(`Version ${left.version_no} compared with version ${right.version_no}`,
    ['Field', `Version ${left.version_no}`, `Version ${right.version_no}`, 'Change'],
    compareVersions(left, right).map((r) => `<tr>
          <td>${esc(r.label)}</td>
          <td><pre class="authoring-json"${SCROLLED_FIELDS.has(r.key) ? ' tabindex="0"' : ''}>${esc(r.left)}</pre></td>
          <td><pre class="authoring-json"${SCROLLED_FIELDS.has(r.key) ? ' tabindex="0"' : ''}>${esc(r.right)}</pre></td>
          <td>${r.changed ? '<span class="authoring-changed">Changed</span>' : 'Same'}</td>
        </tr>`).join(''))}`
    : '<p class="authoring-muted">There is only one version so far.</p>';
  el.innerHTML = card('Versions', section, `${rows.length
    ? table('Versions', ['Version', 'Name', 'Created', 'Author', 'Edited by hand', 'Action'], history)
    : '<p class="authoring-muted">No versions yet.</p>'}${comparison}`, 'versions');
}

function renderMarks() {
  const el = document.getElementById('authoring-section-marks');
  const { item } = detail.data;
  const marks = Array.isArray(detail.data.marks) ? detail.data.marks : [];
  const rows = marks.map((m) => {
    const files = Array.isArray(m.files) ? m.files : [];
    const fileText = files.map((f) => `${FILE_KIND_LABELS[f.kind] || f.kind}: ${f.unusable_at ? 'Unusable' : (FILE_STATE_LABELS[f.state] || f.state)}`).join(' · ');
    const markId = idString(m.id);
    const frames = files.find((f) => f.kind === 'frames' && f.state === 'attached' && idString(f.id));
    // An Open in game link needs the checkpoint's file id: without one it would launch without the checkpoint.
    const checkpoints = files.filter((f) => (f.kind === 'checkpoint_near' || f.kind === 'checkpoint_early') && f.state === 'attached'
      && !f.unusable_at && idString(f.id));
    // Accessible names carry the mark number and contain the visible text (WCAG 2.5.3).
    const actions = markId ? [
      frames ? `<button type="button" class="btn btn-mode" data-action="frames" data-file-id="${esc(idString(frames.id))}" data-mark-id="${esc(markId)}" aria-label="Show frames for mark #${esc(markId)}">Show frames</button>` : '',
      ...checkpoints.map((f) => {
        const url = authoringLaunchUrl({
          systemId: item.system_id, romFilename: m.rom_filename, romHash: m.rom_hash, itemId: detail.itemId, checkpointFileId: f.id,
        });
        const text = `Open in game (${f.kind === 'checkpoint_near' ? 'near' : 'early'} checkpoint)`;
        return url ? `<a class="btn btn-mode" href="${esc(url)}" aria-label="${esc(`${text} for mark #${markId}`)}">${esc(text)}</a>` : '';
      }),
      m.abandoned_at ? '' : `<button type="button" class="btn" data-action="abandon" data-mark-id="${esc(markId)}" aria-label="Abandon mark #${esc(markId)}">Abandon mark</button>`,
    ].filter(Boolean).join(' ') : '';
    return `<tr>
      <td>#${esc(m.id)}</td>
      <td>${esc(markLabel(m))}${m.abandoned_at ? ' <span class="pill">Abandoned</span>' : ''}</td>
      <td>${esc(formatSeconds(m.event_t_ms))}</td>
      <td><code>${esc(m.rom_hash)}</code> · ${esc(m.core_name)}</td>
      <td>${esc(fileText || 'No files')}</td>
      <td><div class="authoring-actions">${actions}</div></td>
    </tr>`;
  }).join('');
  el.innerHTML = card('Marks and checkpoints', null, `${marks.length
    ? table('Marks', ['Mark', 'Role', 'Event time', 'ROM and core', 'Files', 'Actions'], rows)
    : '<p class="authoring-muted">No marks yet. Marks are recorded in author mode.</p>'}
    <div id="authoring-flipbook" class="authoring-flipbook" hidden></div>`);
}

function gateText(value) {
  if (value == null) return 'Not used';
  return value === true || value?.ok === true || value?.passed === true ? 'Passed' : 'Failed';
}

function renderChecks() {
  const el = document.getElementById('authoring-section-checks');
  const section = detail.checks;
  // The history's first row, kept even after its page was let go (loadMore).
  const latest = section.newest || null;
  let body = '<p class="authoring-muted">No check has run for this item yet.</p>';
  if (latest) {
    const perMark = Array.isArray(latest.result?.perMark) ? latest.result.perMark : [];
    const gate = latest.result?.gate || null;
    const timeline = perMark.map((m) => `<tr>
      <td>#${esc(m.markId)}</td>
      <td>${esc(m.role === 'event' ? (POLARITY_LABELS[m.polarity] || m.polarity) : (ROLE_LABELS[m.role] || m.role))}</td>
      <td>${esc(formatSeconds(m.expectedT))}</td>
      <td>${m.firedT == null ? 'Did not fire' : esc(formatSeconds(m.firedT))}</td>
      <td>${m.decodedScore == null ? '–' : esc(m.decodedScore)}</td>
      <td>${m.ok ? 'OK' : esc(CHECK_REASON_LABELS[m.reason] || m.reason || 'Failed')}</td>
    </tr>`).join('');
    const gateRows = gate ? ['start', 'submit', 'cancel'].map((role) => `<tr><td>${GATE_LABELS[role]}</td><td>${gateText(gate[role])}</td></tr>`).join('') : '';
    const history = section.rows.map((c) => `<tr>
      <td>${esc(formatDateTime(c.created_at))}</td>
      <td>${c.passed ? 'Passed' : 'Failed'}</td>
      <td>${esc(c.engine_version)}</td>
      <td>${c.current === false ? 'Outdated' : 'Current'}</td>
    </tr>`).join('');
    body = `<p>${latest.passed ? 'The latest check passed.' : 'The latest check failed.'}${latest.current === false
      ? ' <span class="authoring-changed">Outdated: the evidence changed after this check.</span>' : ''}</p>
      ${perMark.length
    ? table('Firing timeline from the latest check', ['Mark', 'Role', 'Expected', 'Fired', 'Decoded score', 'Result'], timeline)
    : '<p class="authoring-muted">The latest check covered no marks.</p>'}
      ${gate ? table('Leaderboard gate results', ['Gate trigger', 'Result'], gateRows) : ''}
      ${table('Check history', ['Date', 'Result', 'Engine', 'Evidence'], history)}`;
  }
  el.innerHTML = card('Check', section, body, 'checks');
}

function renderTests() {
  const el = document.getElementById('authoring-section-tests');
  const section = detail.tests;
  const rows = section.rows.map((t) => `<tr>
    <td>${esc(formatDateTime(t.created_at))}</td>
    <td>${esc(versionLabel(t.version_no))}</td>
    <td><code>${esc(t.rom_hash)}</code> · ${esc(t.core_name)}</td>
    <td>${esc(t.outcome === 'score' ? `Score ${t.peak_score}` : (OUTCOME_LABELS[t.outcome] || t.outcome))}</td>
    <td>${esc(VERDICT_LABELS[t.verdict] || t.verdict)}</td>
    <td>${t.matched_screen === true ? 'Yes' : t.matched_screen === false ? 'No' : '–'}</td>
    <td>${esc(t.tester_name || 'Unknown')}</td>
  </tr>`).join('');
  el.innerHTML = card('Live tests', section, section.rows.length
    ? table('Live test runs', ['Date', 'Version', 'ROM and core', 'Outcome', 'Verdict', 'Matched the screen', 'Tester'], rows)
    : '<p class="authoring-muted">No live tests yet.</p>', 'tests');
}

function renderAudit() {
  const el = document.getElementById('authoring-section-audit');
  const section = detail.audit;
  const rows = section.rows.map((a) => {
    const details = a.details && Object.keys(a.details).length ? JSON.stringify(a.details) : '';
    return `<tr>
      <td>${esc(formatDateTime(a.created_at))}</td>
      <td>${esc(AUDIT_LABELS[a.action] || a.action)}</td>
      <td>${esc(a.actor_kind === 'system' ? 'System' : (a.actor_name || 'Deleted account'))}</td>
      <td>${a.version_id == null ? '–' : `#${esc(a.version_id)}`}</td>
      <td>${esc(details.length > 160 ? `${details.slice(0, 159)}…` : (details || '–'))}</td>
    </tr>`;
  }).join('');
  el.innerHTML = card('Audit log', section, section.rows.length
    ? table('Audit log', ['Date', 'Action', 'Actor', 'Version', 'Details'], rows)
    : '<p class="authoring-muted">No audit entries yet.</p>', 'audit');
}

const SECTION_RENDERERS = {
  summary: renderSummary, versions: renderVersions, marks: renderMarks,
  checks: renderChecks, tests: renderTests, audit: renderAudit,
};

/** The detail heading's own text (index.html), whenever no item is shown: a failure, a teardown. */
const DETAIL_TITLE = 'Achievement detail';
function resetDetailTitle() {
  const title = document.getElementById('authoring-detail-title');
  if (title) title.textContent = DETAIL_TITLE;
}

function renderDetailContent() {
  const content = document.getElementById('authoring-detail-content');
  const title = document.getElementById('authoring-detail-title');
  if (!content || !detail.data?.item) return;
  if (title) title.textContent = itemName(detail.data.item);
  content.innerHTML = SECTIONS.map((name) => `<section class="authoring-section" id="authoring-section-${name}"></section>`).join('');
  for (const name of SECTIONS) SECTION_RENDERERS[name]();
}

/** One history's first page of `item` as section state; a failed or malformed page (`response_invalid`) is that section's error. */
function historySection(outcome, name, item) {
  const page = outcome.ok ? validPage(outcome.value, name, HISTORY_PAGE_SIZE, item) : null;
  if (!page) {
    const failure = outcome.ok ? responseInvalid() : outcome;
    noteDiagnostic(`history_${name}`, failure.code ?? (failure.timedOut ? 'timeout' : `http_${failure.status}`), failure.cause ?? null);
    return emptyHistory(errorMessage(failure));
  }
  return { ...emptyHistory(), ...page, newest: page.rows[0] ?? null };
}

async function loadDetail(itemId, message = '') {
  const gen = ++detail.gen;
  stopFlipbook();
  // A newer load supersedes the previous one and every page or download it started.
  abortOps(detail);
  detail.loadOp = null;
  const content = document.getElementById('authoring-detail-content');
  const status = document.getElementById('authoring-detail-status');
  if (!content || !status) return false;
  const id = typeof itemId === 'string' ? idString(itemId) : null;
  if (!id) {
    detail.data = null;
    detail.itemId = null;
    content.innerHTML = '';
    resetDetailTitle();
    status.textContent = ERROR_MESSAGES.not_found;
    return false;
  }
  detail.itemId = id;
  status.textContent = message || 'Loading…';
  const base = `${API}/items/${id}`;
  // One operation (one deadline, one AbortController) for the item and its four
  // histories, kept open through rendering, which runs only before its absolute deadline.
  const op = startOp(detail, 'item');
  detail.loadOp = op;
  if (!op) {
    detail.data = null;
    resetHistories();
    content.innerHTML = '';
    resetDetailTitle();
    // The caller's message (an action's result, or that a change may have been saved) stays beside the failure.
    status.textContent = joinSentences(message, CLIENT_ERROR_MESSAGES.op_start_failed);
    return false;
  }
  try {
    const request = { signal: op.signal, deadlineAt: op.deadlineAt };
    let itemOutcome = await sendAuthoringRequest((signal) => api.get(base, { signal }), request);
    // Validated before anything is read from it; a malformed answer clears the view like a failure.
    if (itemOutcome.ok && !validDetail(itemOutcome.value, id)) itemOutcome = responseInvalid();
    let outcomes = [];
    if (itemOutcome.ok && gen === detail.gen) {
      // Four concurrent reads, within the spec 7.1 limit of four per admin (none starts past the deadline).
      outcomes = await Promise.all(HISTORIES.map((name) => sendAuthoringRequest(
        (signal) => api.get(`${base}/${name}?limit=${HISTORY_PAGE_SIZE}`, { signal }), request)));
    }
    const navigatedAway = [itemOutcome, ...outcomes].some((o) => o?.aborted && !o.timedOut);
    if (gen !== detail.gen || navigatedAway) return 'stale';
    if (!itemOutcome.ok || !opLive(op)) {
      // A failure, or a valid answer whose validation or histories ended past the deadline (`timeout`, before any rendering).
      noteDiagnostic('item', itemOutcome.ok ? 'timeout' : (itemOutcome.code ?? (itemOutcome.timedOut ? 'timeout' : `http_${itemOutcome.status}`)),
        itemOutcome.cause ?? null);
      // The previous view is cleared, never left beside a failure.
      detail.data = null;
      resetHistories();
      content.innerHTML = '';
      resetDetailTitle();
      status.textContent = joinSentences(message, itemOutcome.ok ? TIMEOUT_MESSAGE : errorMessage(itemOutcome));
      return false;
    }
    detail.data = itemOutcome.value;
    const { item } = itemOutcome.value;
    HISTORIES.forEach((name, index) => { detail[name] = historySection(outcomes[index], name, item); });
    // Guarded and fenced after rendering: content whose rendering threw or
    // finished after the absolute deadline is taken back, with what the screen retained for it.
    const shown = rendered('item', renderDetailContent);
    if (!shown || !opLive(op)) {
      detail.data = null;
      resetHistories();
      content.innerHTML = '';
      resetDetailTitle();
      status.textContent = joinSentences(message, shown ? TIMEOUT_MESSAGE : RENDER_FAILED_MESSAGE);
      if (shown) noteDiagnostic('item', 'render_deadline', null);
      return false;
    }
    status.textContent = message;
    // An action's re-render detaches the focused control: continue from the heading.
    restoreFocus(document.getElementById('authoring-detail-title'));
    return true;
  } finally {
    endOp(detail, op);
    if (detail.loadOp === op) detail.loadOp = null;
  }
}

function reloadDetail(message) {
  return loadDetail(detail.itemId, message);
}

/** A section whose rendering failed with nothing to fall back to: the message alone, no partial markup. */
function emptySection(name) {
  const el = document.getElementById(`authoring-section-${name}`);
  if (el) el.innerHTML = `<p class="authoring-error" role="alert">${esc(RENDER_FAILED_MESSAGE)}</p>`;
}

/**
 * One more page of a history, or (`fromStart`) its first page again ("Show newest"): single-flight per
 * history, applied only while the item load, the section and its cursor are current. Retained state is
 * bounded without hiding any entry (spec 12): past HISTORY_ROWS_MAX rows, the first rows shown are let
 * go (counted in `skipped`) and "Show more" still pages on through the whole history.
 */
async function loadMore(name, { fromStart = false } = {}) {
  const section = detail[name];
  if (!HISTORIES.includes(name) || !section || !detail.itemId || section.pending) return;
  if (!fromStart && !section.nextCursor) return;
  const gen = detail.gen;
  const cursor = fromStart ? null : section.nextCursor;
  const op = startOp(detail, `history_${name}`);
  if (!op) {
    // Nothing was sent; the rows already shown stay, with the reason.
    section.error = CLIENT_ERROR_MESSAGES.op_start_failed;
    if (!rendered(`history_${name}`, SECTION_RENDERERS[name])) emptySection(name);
    return;
  }
  section.pending = op;
  const sectionSelector = `#authoring-section-${name}`;
  for (const button of document.querySelectorAll(`${sectionSelector} [data-action="more"], ${sectionSelector} [data-action="newest"]`)) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }
  try {
    const query = cursor === null ? `limit=${HISTORY_PAGE_SIZE}` : `limit=${HISTORY_PAGE_SIZE}&cursor=${encodeURIComponent(cursor)}`;
    let outcome = await sendAuthoringRequest((signal) => api.get(`${API}/items/${detail.itemId}/${name}?${query}`, { signal }),
      { signal: op.signal, deadlineAt: op.deadlineAt });
    section.pending = null;
    if (gen !== detail.gen || detail[name] !== section || (!fromStart && section.nextCursor !== cursor) || !detail.data?.item) return;
    if (outcome.aborted && !outcome.timedOut) return;
    const page = outcome.ok ? validPage(outcome.value, name, HISTORY_PAGE_SIZE, detail.data.item) : null;
    if (outcome.ok && !page) outcome = responseInvalid();
    // Rendering is part of the operation: a page that completes after its absolute deadline is not applied.
    if (outcome.ok && !opLive(op)) outcome = abortedOutcome(op.signal, { timedOut: true });
    if (outcome.ok) {
      const joined = fromStart ? page.rows : [...section.rows, ...page.rows];
      const drop = Math.max(0, joined.length - HISTORY_ROWS_MAX);
      detail[name] = {
        ...page, rows: drop ? joined.slice(drop) : joined, error: null, pending: null,
        skipped: fromStart ? 0 : section.skipped + drop,
        newest: fromStart ? (page.rows[0] ?? null) : section.newest,
      };
    } else {
      if (!outcome.timedOut) noteDiagnostic(`history_${name}`, outcome.code ?? `http_${outcome.status}`, outcome.cause ?? null);
      section.error = errorMessage(outcome);   // the rows already shown stay
    }
    let shown = rendered(`history_${name}`, SECTION_RENDERERS[name]);
    if (outcome.ok && (!shown || !opLive(op))) {
      // The page's rendering threw or finished after the absolute deadline:
      // take it back and show the history as it was, with the reason.
      section.error = shown ? TIMEOUT_MESSAGE : RENDER_FAILED_MESSAGE;
      if (shown) noteDiagnostic(`history_${name}`, 'render_deadline', null);
      detail[name] = section;
      shown = rendered(`history_${name}`, SECTION_RENDERERS[name]);
    }
    if (!shown) emptySection(name);
    const sectionEl = document.getElementById(`authoring-section-${name}`);
    restoreFocus(sectionEl?.querySelector('[data-action="more"]') || sectionEl?.querySelector('.authoring-heading'));
  } finally {
    if (section.pending === op) section.pending = null;
    endOp(detail, op);
  }
}

// ── Flipbook ──

/** Stop the flipbook: its timer, frames and object URL are released before any DOM work. */
function stopFlipbook() {
  const flip = detail.flip;
  detail.flip = null;
  if (flip) {
    clearInterval(flip.timer);
    const { url } = flip;
    Object.assign(flip, { timer: null, url: null, frames: [] });
    flip.op?.abort(navAbort());
    try {
      if (url) URL.revokeObjectURL(url);
    } catch (error) {
      noteDiagnostic('frames', 'revoke_failed', error);
    }
  }
  const box = document.getElementById('authoring-flipbook');
  if (box) {
    box.innerHTML = '';
    box.hidden = true;
  }
}

/** Stop the flipbook, then show `message` in its place and return focus to `opener`. */
function flipbookMessage(message, opener) {
  stopFlipbook();
  const box = document.getElementById('authoring-flipbook');
  if (box) {
    box.hidden = false;
    box.innerHTML = `<p class="authoring-error" role="alert">${esc(message)}</p>`;
  }
  if (opener?.isConnected) opener.focus();
}

/** Blob, URL or DOM work for `flip` threw: kept as `render_failed`, and the flipbook released if still shown. */
function failFlipbook(flip, error) {
  noteDiagnostic('frames', 'render_failed', error);
  if (detail.flip === flip) flipbookMessage(FRAMES_RENDER_MESSAGE, flip.opener);
}

/** Close frames from its own button: stop, then return focus to the button that opened it. */
function closeFlipbook() {
  const opener = detail.flip?.opener || null;
  stopFlipbook();
  if (opener?.isConnected) opener.focus();
}

/** Install one frame (first or later, playback included); a throw releases the whole flipbook (failFlipbook). */
function showFrame(index) {
  const flip = detail.flip;
  if (!flip?.frames.length) return;
  try {
    flip.index = Math.max(0, Math.min(flip.frames.length - 1, index));
    const frame = flip.frames[flip.index];
    const old = flip.url;
    flip.url = null;
    if (old) URL.revokeObjectURL(old);
    flip.url = URL.createObjectURL(new Blob([frame.jpeg], { type: 'image/jpeg' }));
    const label = `Frame ${flip.index + 1} of ${flip.frames.length}`;
    const img = document.getElementById('authoring-flip-image');
    const caption = document.getElementById('authoring-flip-caption');
    const range = document.getElementById('authoring-flip-range');
    if (img) {
      img.src = flip.url;
      img.alt = `${label} for mark #${flip.markId}`;
    }
    if (caption) caption.textContent = `${label} · ${formatSeconds(frame.t)}`;
    if (range) {
      range.value = String(flip.index + 1);
      range.setAttribute('aria-valuetext', label);
    }
  } catch (error) {
    failFlipbook(flip, error);
  }
}

function toggleFlipPlay(button) {
  const flip = detail.flip;
  if (!flip?.frames.length) return;
  if (flip.timer) {
    clearInterval(flip.timer);
    flip.timer = null;
    button.textContent = 'Play';
    return;
  }
  button.textContent = 'Pause';
  flip.timer = setInterval(() => {
    if (flip.index >= flip.frames.length - 1) {
      clearInterval(flip.timer);
      flip.timer = null;
      button.textContent = 'Play';
      return;
    }
    showFrame(flip.index + 1);
  }, FLIP_INTERVAL_MS);
}

async function showFrames(fileId, markId, opener) {
  stopFlipbook();
  const box = document.getElementById('authoring-flipbook');
  const id = idString(fileId);
  if (!box || !id) return;
  const gen = detail.gen;
  // One download at a time (spec 7.12 allows one per admin), under its own
  // operation, which stays open through decoding and installation: the
  // operation finishes only after its owned work is done.
  const op = startOp(detail, 'frames');
  if (!op) {
    flipbookMessage(CLIENT_ERROR_MESSAGES.op_start_failed, opener);
    return;
  }
  const flip = { frames: [], index: 0, url: null, timer: null, markId, opener, op };
  detail.flip = flip;
  box.hidden = false;
  box.innerHTML = '<p class="admin-status" role="status">Loading frames…</p>';
  try {
    const outcome = await sendAuthoringRequest((signal) => fetchFileBytes(id, signal, op.deadlineAt),
      { signal: op.signal, deadlineAt: op.deadlineAt });
    if (gen !== detail.gen || detail.flip !== flip || (outcome.aborted && !outcome.timedOut)) return;
    if (!outcome.ok) {
      if (!outcome.timedOut) noteDiagnostic('frames', outcome.code ?? `http_${outcome.status}`, outcome.cause ?? null);
      box.innerHTML = `<p class="authoring-error" role="alert">${esc(outcome.timedOut ? FRAMES_TIMEOUT_MESSAGE : errorMessage(outcome))}</p>`;
      return;
    }
    try {
      flip.frames = flipbookFrames(outcome.value);
    } catch (error) {
      // Task 4's EvidenceFormatError (or any decode failure) is kept for diagnosis; the admin sees one message.
      noteDiagnostic('frames_decode', typeof error?.code === 'string' ? error.code : 'decode_failed', error);
      box.innerHTML = '<p class="authoring-error" role="alert">These frames could not be read.</p>';
      return;
    }
    // Decoding is synchronous: check the absolute deadline itself, not only the timer.
    if (!opLive(op)) {
      flip.frames = [];
      const navigated = op.signal.aborted && op.signal.reason?.name === 'AbortError';
      if (!navigated && gen === detail.gen && detail.flip === flip) {
        box.innerHTML = `<p class="authoring-error" role="alert">${esc(FRAMES_TIMEOUT_MESSAGE)}</p>`;
      }
      return;
    }
    // Installation (the DOM, the Blob and its object URL) is guarded and
    // fenced: a flipbook whose installation threw, or finished after the
    // absolute deadline, is released (timer, frames, object URL) and focus
    // returned to its opener.
    try {
      renderFlipbook(box, flip, markId);
    } catch (error) {
      failFlipbook(flip, error);
      return;
    }
    if (!opLive(op) && detail.flip === flip) {
      noteDiagnostic('frames', 'render_deadline', null);
      flipbookMessage(FRAMES_TIMEOUT_MESSAGE, opener);
    }
  } finally {
    endOp(detail, op);
    flip.op = null;
  }
}

function renderFlipbook(box, flip, markId) {
  if (!flip.frames.length) {
    box.innerHTML = '<p class="authoring-muted">This frame sequence is empty.</p>';
    return;
  }
  box.innerHTML = `
    <h4 class="authoring-heading" id="authoring-flip-heading" tabindex="-1">Frames for mark #${esc(markId)}</h4>
    <figure class="authoring-figure">
      <img id="authoring-flip-image" alt="">
      <figcaption id="authoring-flip-caption"></figcaption>
    </figure>
    <div class="authoring-flip-controls">
      <button type="button" class="btn btn-mode" data-action="flip-prev">Previous frame</button>
      <button type="button" class="btn btn-mode" data-action="flip-play">Play</button>
      <button type="button" class="btn btn-mode" data-action="flip-next">Next frame</button>
      <label for="authoring-flip-range">Frame</label>
      <input id="authoring-flip-range" type="range" min="1" max="${flip.frames.length}" value="1" step="1">
      <button type="button" class="btn btn-mode" data-action="flip-close">Close frames</button>
    </div>`;
  box.querySelector('#authoring-flip-range').addEventListener('input', (event) => showFrame(Number(event.target.value) - 1));
  showFrame(0);
  box.querySelector('#authoring-flip-heading')?.focus();   // absent when the first frame failed (failFlipbook)
}

// ── Detail actions ──

function onDetailClick(event) {
  const target = event.target.closest('[data-action]');
  if (!target || !detail.data?.item || !detail.itemId) return;
  const { item } = detail.data;
  // The validated id from the route, never a string from the response.
  const itemPath = `${API}/items/${detail.itemId}`;
  switch (target.dataset.action) {
    case 'publish':
      openPublishDialog(item, target);
      break;
    case 'unpublish':
      confirmAndSend({
        title: `Unpublish ${itemPhrase(item)}`,
        description: item.kind === 'score'
          ? 'Scores stop counting right away. High scores are kept but hidden, and come back if you publish again.'
          : 'Players stop earning it right away and stop seeing it within 10 minutes. Existing unlocks are kept and come back if you publish again.',
        confirmLabel: 'Unpublish',
        opener: target,
        send: (signal) => api.post(`${itemPath}/unpublish`, { row_version: item.row_version }, { signal }),
        publication: true,   // reported only with committed: true, with the refresh report checked part by part
        onSuccess: (value) => reloadDetail(joinSentences('Unpublished.', refreshSummary(value))),
      });
      break;
    case 'delete':
      confirmAndSend({
        title: `Delete ${itemPhrase(item)}`,
        description: 'This deletes every version, mark, check and live test of this item, and its evidence files. It cannot be undone.',
        confirmLabel: 'Delete',
        opener: target,
        send: (signal) => api.delete(`${itemPath}?row_version=${encodeURIComponent(item.row_version)}`, undefined, { signal }),
        // Task 17 answers a delete like a publication ({ committed, correlationId, refresh, ... }).
        publication: true,
        onSuccess: (value) => {
          list.notice = joinSentences(`Deleted ${itemPhrase(item)}.`, refreshSummary(value));
          navigate('/admin/achievements');
        },
        // A delete whose outcome is unknown: the list shows whether the item is still there (L1-2).
        onUnknown: (timedOut) => {
          list.notice = timedOut
            ? `The server took too long to answer, so ${itemPhrase(item)} may have been deleted. Check the list before trying again.`
            : `The server may have deleted ${itemPhrase(item)}. Check the list before trying again.`;
          navigate('/admin/achievements');
        },
      });
      break;
    case 'restore': {
      const version = detail.versions.rows.find((v) => String(v.id) === target.dataset.versionId);
      if (!version || !idString(version.id)) return;
      confirmAndSend({
        title: `Restore version ${version.version_no}`,
        description: `Create a new draft version that copies version ${version.version_no}. The current version stays in the history, and the new version needs its own check before it can be published.`,
        confirmLabel: 'Restore version',
        opener: target,
        send: (signal) => api.post(`${itemPath}/restore`, { row_version: item.row_version, version_id: version.id }, { signal }),
        onSuccess: () => reloadDetail(`Restored version ${version.version_no} as a new draft version.`),
      });
      break;
    }
    case 'abandon': {
      const mark = (detail.data.marks || []).find((m) => String(m.id) === target.dataset.markId);
      const markId = idString(mark?.id);
      if (!markId) return;
      confirmAndSend({
        title: `Abandon mark #${markId}`,
        description: mark.published
          ? 'Future checks will ignore this mark. A publication relies on it, so its evidence files are kept.'
          : 'Future checks will ignore this mark, and its evidence files are deleted.',
        confirmLabel: 'Abandon mark',
        opener: target,
        send: (signal) => api.post(`${API}/marks/${markId}/abandon`, {}, { signal }),
        onSuccess: () => reloadDetail(`Abandoned mark #${markId}. Run the check again before publishing.`),
      });
      break;
    }
    case 'frames':
      showFrames(target.dataset.fileId, target.dataset.markId, target);
      break;
    case 'flip-prev':
      showFrame((detail.flip?.index ?? 0) - 1);
      break;
    case 'flip-next':
      showFrame((detail.flip?.index ?? 0) + 1);
      break;
    case 'flip-play':
      toggleFlipPlay(target);
      break;
    case 'flip-close':
      closeFlipbook();
      break;
    case 'more':
      loadMore(target.dataset.section);
      break;
    case 'newest':
      loadMore(target.dataset.section, { fromStart: true });
      break;
    default:
      break;
  }
}

function onDetailChange(event) {
  const select = event.target.closest('[data-compare]');
  if (!select || !detail.data) return;
  detail.compare[select.dataset.compare] = select.value;
  if (!rendered('history_versions', renderVersions)) emptySection('versions');
  document.getElementById(select.id)?.focus();
}

function bindDetail() {
  if (detail.bound) return;
  const content = document.getElementById('authoring-detail-content');
  if (!content) return;
  detail.bound = true;
  content.addEventListener('click', onDetailClick);
  content.addEventListener('change', onDetailChange);
}

export function renderDetail(params, ctx) {
  bindDetail();
  // Teardown callbacks are synchronous (router contract): abort, clear, close,
  // then release everything the screen retained (item data, histories, content).
  ctx?.onTeardown?.(() => {
    detail.gen += 1;
    stopFlipbook();
    abortOps(detail);
    closeDialogs();
    detail.data = null;
    detail.compare = { left: null, right: null };
    resetHistories();
    const content = document.getElementById('authoring-detail-content');
    if (content) content.innerHTML = '';
    // The next item's load starts from the screen's own heading, never the previous item's name.
    resetDetailTitle();
    // A status a continuation set just before the teardown (a reload's message) goes with the screen too.
    const status = document.getElementById('authoring-detail-status');
    if (status) status.textContent = '';
  });
  detail.compare = { left: null, right: null };
  return loadDetail(params?.id);
}
